import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import styled from "styled-components";
import { toast } from "react-hot-toast";
import {
  State,
  Coordinates,
  GuideLayer,
  MapVersion,
  MapStyle,
  MAP_STYLES,
  RegionCode,
} from "../types";
import { TemplateState } from "../types/templates";
import TemplateManager from "./TemplateManager";
import { uploadFileToFirebase } from "../utils/imageUtils";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { getMapVersions, getVersionDiff } from "../services/versionService";

interface StatePanelProps {
  states: State[];
  selectedState: State | null;
  onStateSelect: (state: State | null) => void;
  onStateUpdate: (state: State) => void;
  onStateDelete: (stateId: string) => void;
  onAddPredefinedState: (state: Partial<State>) => void;
  onTemplateSelect: (states: TemplateState[]) => void;
  editingStateId: string | null;
  setEditingStateId: (id: string | null) => void;
  drawingMode: boolean;
  setDrawingMode: (mode: boolean) => void;
  onSaveMap: () => void;
  onExportTemplate: () => void;
  onImportTemplate: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearAllStates?: () => void;
  guideLayers?: GuideLayer[];
  onAddGuideLayer?: (guideLayer: GuideLayer) => void;
  onUpdateGuideLayer?: (id: string, updates: Partial<GuideLayer>) => void;
  onDeleteGuideLayer?: (id: string) => void;
  onToggleGuideLayer?: (id: string) => void;
  mapName?: string;
  setMapName?: (name: string) => void;
  mapDescription?: string;
  setMapDescription?: (description: string) => void;
  mapRegion?: RegionCode;
  onRegionChange?: (region: RegionCode) => void;
  isPublic?: boolean;
  setIsPublic?: (isPublic: boolean) => void;
  showCapitals?: boolean;
  setShowCapitals?: (show: boolean) => void;
  showCapitalNames?: boolean;
  setShowCapitalNames?: (show: boolean) => void;
  showStateNames?: boolean;
  setShowStateNames?: (show: boolean) => void;
  showStateFill?: boolean;
  setShowStateFill?: (show: boolean) => void;
  mapStyle?: MapStyle;
  setMapStyle?: (style: MapStyle) => void;
  isEditMode?: boolean;
  isSettingCapital?: boolean;
  setIsSettingCapital?: (isSetting: boolean) => void;
  onCapitalCoordinateSet?: (stateId: string, coordinates: Coordinates) => void;
  setEditingGuideLayerId?: (id: string | null) => void;
  mapRef?: React.RefObject<any>;
  currentThumbnail?: string;
  onCustomThumbnailFile?: (file: File | null) => void;
  mapId?: string;
  approvalRequested?: boolean;
  isApproved?: boolean;
  isRejected?: boolean;
  rejectionReason?: string;
  version?: number;
  approvedVersion?: number;
  onRestoreVersion?: (version: MapVersion) => void;
  // Border display props
  showBorder?: boolean;
  setShowBorder?: (show: boolean) => void;
  customBorder?: Coordinates[][] | null;
  isEditingBorder?: boolean;
  onStartEditingBorder?: () => void;
  onFinishEditingBorder?: () => void;
  onDeleteBorder?: () => void;
}

// ============================================
// NEW MODERN DESIGN - Inspired by Figma/VS Code
// ============================================

// Main Container - Modern split layout
const PanelContainer = styled.div<{ $isDarkMode: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow: hidden;
  transition: background 0.3s ease, color 0.3s ease;
`;

// Top Bar - Clean header with title and actions
const TopBar = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#f5f5f5")};
  border-bottom: 1px solid
    ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  min-height: 48px;
  flex-shrink: 0;
  transition: background 0.3s ease, border-color 0.3s ease;
`;

const TopBarTitle = styled.div<{ $isDarkMode: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.3s ease;
`;

const TopBarActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconButton = styled.button<{ $variant?: "danger" | "primary" }>`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${(props) =>
    props.$variant === "danger"
      ? "#f44336"
      : props.$variant === "primary"
      ? "#2196f3"
      : "#b0b0b0"};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$variant === "danger"
        ? "rgba(244, 67, 54, 0.1)"
        : props.$variant === "primary"
        ? "rgba(33, 150, 243, 0.1)"
        : "rgba(255, 255, 255, 0.05)"};
    color: ${(props) =>
      props.$variant === "danger"
        ? "#ff6b6b"
        : props.$variant === "primary"
        ? "#42a5f5"
        : "#ffffff"};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Main Content Area - Split into sidebar and content
const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

// Sidebar Navigation - Icon-based (like VS Code)
const Sidebar = styled.div<{ $isDarkMode: boolean }>`
  width: 56px;
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#f5f5f5")};
  border-right: 1px solid
    ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  display: flex;
  transition: background 0.3s ease, border-color 0.3s ease;
  flex-direction: column;
  padding: 8px 0;
  gap: 4px;
  flex-shrink: 0;
`;

const SidebarButton = styled.button<{
  $active?: boolean;
  $isDarkMode: boolean;
}>`
  width: 40px;
  height: 40px;
  margin: 0 auto;
  border: none;
  border-radius: 8px;
  background: ${(props) => (props.$active ? "#2196f3" : "transparent")};
  color: ${(props) =>
    props.$active ? "#ffffff" : props.$isDarkMode ? "#b0b0b0" : "#666666"};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${(props) =>
      props.$active
        ? "#2196f3"
        : props.$isDarkMode
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.05)"};
    color: ${(props) =>
      props.$active ? "#ffffff" : props.$isDarkMode ? "#ffffff" : "#1a1a1a"};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${(props) =>
    props.$active &&
    `
    &::before {
      content: '';
      position: absolute;
      left: -8px;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 20px;
      background: #2196f3;
      border-radius: 0 2px 2px 0;
    }
  `}
`;

// Content Panel - Main content area
const ContentPanel = styled.div<{ $isDarkMode: boolean }>`
  flex: 1;
  overflow-y: auto;
  background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#ffffff")};
  padding: 0;
  transition: background 0.3s ease;
`;

const ContentSection = styled.div<{ $active?: boolean }>`
  display: ${(props) => (props.$active ? "block" : "none")};
  padding: 12px 16px;
  animation: fadeIn 0.2s ease;

  &[data-compact="true"] {
    padding: 12px 8px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Getting Started Card - Modern gradient card
const GettingStartedCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    animation: pulse 3s ease-in-out infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.3;
    }
    50% {
      opacity: 0.6;
    }
  }
`;

const GettingStartedTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

const GettingStartedDescription = styled.div`
  font-size: 14px;
  opacity: 0.95;
  margin-bottom: 20px;
  line-height: 1.6;
  position: relative;
  z-index: 1;
`;

const GettingStartedActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  z-index: 1;
`;

const GettingStartedButton = styled.button<{ $primary?: boolean }>`
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${(props) =>
    props.$primary ? "white" : "rgba(255, 255, 255, 0.2)"};
  color: ${(props) => (props.$primary ? "#667eea" : "white")};
  backdrop-filter: blur(10px);

  &:hover {
    background: ${(props) =>
      props.$primary ? "#f5f5f5" : "rgba(255, 255, 255, 0.3)"};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DismissButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Card-based sections
const Card = styled.div<{ $isDarkMode: boolean }>`
  background: ${(props) => (props.$isDarkMode ? "#252525" : "#f9f9f9")};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.$isDarkMode ? "#404040" : "#d0d0d0")};
    box-shadow: 0 4px 12px
      ${(props) =>
        props.$isDarkMode ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.1)"};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardTitle = styled.div<{ $isDarkMode: boolean }>`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  display: flex;
  align-items: center;
  gap: 10px;
  transition: color 0.3s ease;
`;

const CardIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
`;

// Section component for Map Details and Thumbnail (non-card style)
const Section = styled.div<{ $isDarkMode: boolean }>`
  padding: 20px 0;
  border-bottom: 1px solid
    ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  transition: border-color 0.3s ease;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
`;

const SectionIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

// Form Elements
const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormSection = styled.div<{ $isDarkMode: boolean }>`
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid
    ${(props) => (props.$isDarkMode ? "#252525" : "#e0e0e0")};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
  transition: border-color 0.3s ease;
`;

const FormSectionTitle = styled.div<{ $isDarkMode: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#888" : "#666")};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
  transition: color 0.3s ease;
`;

const Label = styled.label<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#666")};
  margin-bottom: 8px;
  transition: color 0.3s ease;
`;

const LabelText = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FieldHint = styled.span<{ $isDarkMode?: boolean }>`
  font-size: 11px;
  color: ${(props) => (props.$isDarkMode !== false ? "#666" : "#999")};
  font-weight: 400;
  margin-left: auto;
  transition: color 0.3s ease;
`;

const Input = styled.input<{ $isDarkMode: boolean }>`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#d0d0d0")};
  border-radius: 8px;
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    border-color: ${(props) => (props.$isDarkMode ? "#404040" : "#b0b0b0")};
  }

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
    background: ${(props) => (props.$isDarkMode ? "#252525" : "#ffffff")};
  }

  &::placeholder {
    color: ${(props) => (props.$isDarkMode ? "#666" : "#999")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea<{ $isDarkMode: boolean }>`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#d0d0d0")};
  border-radius: 8px;
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  font-size: 14px;
  resize: vertical;
  min-height: 140px;
  transition: all 0.2s ease;
  font-family: inherit;
  box-sizing: border-box;
  line-height: 1.6;

  &:hover:not(:disabled) {
    border-color: ${(props) => (props.$isDarkMode ? "#404040" : "#b0b0b0")};
  }

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
    background: ${(props) => (props.$isDarkMode ? "#252525" : "#ffffff")};
  }

  &::placeholder {
    color: ${(props) => (props.$isDarkMode ? "#666" : "#999")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`;

// Map Style Gallery Components
const MapStyleGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-top: 8px;
`;

const MapStyleCard = styled.button<{
  $isSelected: boolean;
  $isDarkMode: boolean;
  $color: string;
}>`
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  border: 2px solid
    ${(props) =>
      props.$isSelected
        ? "#2196f3"
        : props.$isDarkMode
        ? "#2d2d2d"
        : "#d0d0d0"};
  border-radius: 8px;
  background: ${(props) => props.$color};
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  box-shadow: ${(props) =>
    props.$isSelected
      ? "0 0 0 3px rgba(33, 150, 243, 0.2)"
      : "0 1px 3px rgba(0, 0, 0, 0.1)"};

  &:hover {
    border-color: ${(props) =>
      props.$isSelected
        ? "#2196f3"
        : props.$isDarkMode
        ? "#404040"
        : "#b0b0b0"};
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.$isSelected
        ? "0 0 0 3px rgba(33, 150, 243, 0.3)"
        : "0 4px 8px rgba(0, 0, 0, 0.15)"};
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0, 0, 0, 0.3) 60%,
      rgba(0, 0, 0, 0.6) 100%
    );
    pointer-events: none;
  }

  ${(props) =>
    props.$isSelected &&
    `
    &::after {
      content: '✓';
      position: absolute;
      top: 6px;
      right: 6px;
      width: 20px;
      height: 20px;
      background: #2196f3;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
      z-index: 2;
    }
  `}
`;

const MapStylePreview = styled.div<{ $color: string; $thumbnail?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) => props.$color};
  background-image: ${(props) => {
    if (props.$thumbnail) {
      return `url(${props.$thumbnail})`;
    }
    // Create a subtle pattern based on color
    const isDark = props.$color.includes("#1") || props.$color.includes("#2");
    return isDark
      ? "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.05) 0%, transparent 50%)"
      : "radial-gradient(circle at 20% 30%, rgba(0,0,0,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.03) 0%, transparent 50%)";
  }};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
`;

const MapStyleLabel = styled.span<{ $isDarkMode: boolean }>`
  position: relative;
  z-index: 1;
  color: white;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  padding: 4px 6px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MapStyleDescription = styled.div<{ $isDarkMode: boolean }>`
  font-size: 11px;
  color: ${(props) => (props.$isDarkMode ? "#888" : "#666")};
  margin-top: 8px;
  text-align: center;
  line-height: 1.4;
`;

// Primary Action Button
const PrimaryButton = styled.button<{
  $active?: boolean;
  $fullWidth?: boolean;
}>`
  padding: 0 10px;
  border: none;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: ${(props) => (props.$active ? "#4caf50" : "#2196f3")};
  color: white;
  width: ${(props) => (props.$fullWidth ? "100%" : "auto")};
  height: 28px;
  box-sizing: border-box;
  box-shadow: ${(props) =>
    props.$active
      ? "0 2px 6px rgba(76, 175, 80, 0.3)"
      : "0 2px 6px rgba(33, 150, 243, 0.3)"};

  &:hover {
    background: ${(props) => (props.$active ? "#45a049" : "#1976d2")};
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.$active
        ? "0 3px 10px rgba(76, 175, 80, 0.4)"
        : "0 3px 10px rgba(33, 150, 243, 0.4)"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    display: block;
  }
`;

// Secondary Button
const SecondaryButton = styled.button<{ $isDarkMode: boolean }>`
  padding: 0 10px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#404040" : "#d0d0d0")};
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: transparent;
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  height: 28px;
  box-sizing: border-box;
  white-space: nowrap;

  &:hover {
    background: #2d2d2d;
    border-color: #505050;
    color: #ffffff;
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    display: block;
  }
`;

// Professional Layer Panel - Inspired by Photoshop/Draw.io
const LayerPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const LayerPanelHeader = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid
    ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#f5f5f5")};
  transition: background 0.3s ease, border-color 0.3s ease;
`;

const LayerPanelHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const LayerSearchContainer = styled.div<{ $visible: boolean }>`
  display: ${(props) => (props.$visible ? "block" : "none")};
  margin-top: 8px;
  animation: ${(props) => (props.$visible ? "slideDown 0.2s ease" : "none")};

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LayerSearchInput = styled.input<{ $isDarkMode?: boolean }>`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#d0d0d0")};
  border-radius: 6px;
  background: ${(props) => (props.$isDarkMode ? "#252525" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  font-size: 12px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2196f3;
    background: ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#ffffff")};
  }

  &::placeholder {
    color: ${(props) => (props.$isDarkMode ? "#666" : "#999")};
  }
`;

const LayerPanelTitle = styled.div<{ $isDarkMode?: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.3s ease;
`;

const LayerPanelActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;

const LayerPanelActionButton = styled.button<{ $isDarkMode: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#666")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LayerGroup = styled.div<{ $isPrimary?: boolean }>`
  margin-bottom: ${(props) => (props.$isPrimary ? "20px" : "16px")};
`;

const LayerGroupHeader = styled.div<{
  $isPrimary?: boolean;
  $isDarkMode: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => (props.$isPrimary ? "12px 14px" : "10px 12px")};
  background: ${(props) =>
    props.$isPrimary
      ? props.$isDarkMode
        ? "#2a2a2a"
        : "#e3f2fd"
      : props.$isDarkMode
      ? "#252525"
      : "#f5f5f5"};
  border-radius: 8px;
  margin-bottom: ${(props) => (props.$isPrimary ? "12px" : "8px")};
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: ${(props) => (props.$isPrimary ? "3px solid #2196f3" : "none")};

  &:hover {
    background: ${(props) =>
      props.$isPrimary
        ? props.$isDarkMode
          ? "#2f2f2f"
          : "#d1e7ff"
        : props.$isDarkMode
        ? "#2a2a2a"
        : "#eeeeee"};
  }
`;

const LayerGroupTitle = styled.div<{
  $isPrimary?: boolean;
  $isDarkMode?: boolean;
}>`
  font-size: ${(props) => (props.$isPrimary ? "12px" : "11px")};
  font-weight: ${(props) => (props.$isPrimary ? "700" : "600")};
  color: ${(props) =>
    props.$isPrimary
      ? props.$isDarkMode
        ? "#e0e0e0"
        : "#1a1a1a"
      : props.$isDarkMode
      ? "#b0b0b0"
      : "#666"};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.3s ease;
`;

const LayerGroupCount = styled.div<{
  $isPrimary?: boolean;
  $isDarkMode: boolean;
}>`
  font-size: ${(props) => (props.$isPrimary ? "11px" : "10px")};
  color: ${(props) =>
    props.$isPrimary
      ? props.$isDarkMode
        ? "#888"
        : "#666"
      : props.$isDarkMode
      ? "#666"
      : "#999"};
  background: ${(props) =>
    props.$isPrimary
      ? props.$isDarkMode
        ? "#1e1e1e"
        : "#ffffff"
      : props.$isDarkMode
      ? "#1a1a1a"
      : "#f0f0f0"};
  padding: ${(props) => (props.$isPrimary ? "3px 8px" : "2px 6px")};
  border-radius: 10px;
  font-weight: ${(props) => (props.$isPrimary ? "600" : "500")};
  transition: background 0.3s ease, color 0.3s ease;
`;

const LayerGroupActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LayerGroupAddButton = styled.button<{ $isDarkMode?: boolean }>`
  width: 28px;
  height: 28px;
  border: 1.5px solid ${(props) => (props.$isDarkMode ? "#444" : "#ddd")};
  border-radius: 50%;
  background: ${(props) => (props.$isDarkMode ? "#2a2a2a" : "#f8f8f8")};
  color: ${(props) => (props.$isDarkMode ? "#aaa" : "#666")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    background: ${(props) => (props.$isDarkMode ? "#333" : "#e8e8e8")};
    border-color: ${(props) => (props.$isDarkMode ? "#555" : "#bbb")};
    color: ${(props) => (props.$isDarkMode ? "#fff" : "#333")};
    transform: scale(1.05);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const LayerList = styled.div<{ $isDarkMode?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 2px 0;
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#f5f5f5")};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => (props.$isDarkMode ? "#444" : "#ccc")};
    border-radius: 3px;

    &:hover {
      background: ${(props) => (props.$isDarkMode ? "#555" : "#bbb")};
    }
  }
`;

// Professional Layer Item - Like Photoshop/Draw.io
const ProfessionalLayerItem = styled.div<{
  $selected?: boolean;
  $visible?: boolean;
  $isDragging?: boolean;
  $isEditing?: boolean;
  $isDarkMode: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 5px 8px 5px 3px;
  background: ${(props) => {
    if (props.$isEditing) return props.$isDarkMode ? "#2a3a2a" : "#e8f5e9";
    return props.$selected
      ? props.$isDarkMode
        ? "#2d2d2d"
        : "#e3f2fd"
      : "transparent";
  }};
  border-left: 3px solid
    ${(props) => {
      if (props.$isEditing) return "#ff9800";
      return props.$selected ? "#2196f3" : "transparent";
    }};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  opacity: ${(props) => (props.$visible ? 1 : 0.4)};
  position: relative;
  min-height: 36px;
  margin-bottom: 1px;
  gap: 6px;
  box-shadow: ${(props) =>
    props.$isEditing ? "0 0 0 1px rgba(255, 152, 0, 0.3)" : "none"};

  &:hover {
    background: ${(props) => {
      if (props.$isEditing) return props.$isDarkMode ? "#2a3a2a" : "#e8f5e9";
      return props.$selected
        ? props.$isDarkMode
          ? "#2d2d2d"
          : "#e3f2fd"
        : props.$isDarkMode
        ? "#252525"
        : "#f5f5f5";
    }};
  }

  ${(props) =>
    props.$isDragging &&
    `
    opacity: 0.5;
    transform: scale(0.98);
  `}
`;

const LayerControls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  align-self: center;
  margin-top: 0px;
  width: 66px;
  justify-content: flex-end;

  ${ProfessionalLayerItem}:hover & {
    opacity: 1;
  }
`;

const LayerThumbnail = styled.div<{ $color: string; $type: "state" | "guide" | "border" }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: ${(props) =>
    props.$type === "guide" || props.$type === "border"
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : props.$color};
  border: 1px solid
    ${(props) =>
      props.$type === "guide" ? "#2196f3" : "rgba(255, 255, 255, 0.2)"};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: grab;
  transition: all 0.15s ease;

  &:active {
    cursor: grabbing;
    transform: scale(0.95);
  }

  &:hover {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) =>
      props.$type === "guide"
        ? 'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" opacity="0.3"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>\')'
        : "none"};
    background-size: 60%;
    background-position: center;
    background-repeat: no-repeat;
  }
`;

const LayerContent = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-right: 0px;
  overflow: hidden;
`;

const LayerNameInput = styled.input<{ $isDarkMode?: boolean }>`
  background: transparent;
  border: none;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  font-size: 14px;
  font-weight: 500;
  padding: 4px 6px;
  border-radius: 4px;
  width: 100%;
  font-family: inherit;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#ffffff")};
    border: 1px solid #2196f3;
  }

  &:hover:not(:focus) {
    background: ${(props) =>
      props.$isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)"};
  }
`;

const LayerNameDisplay = styled.div<{
  $isLong?: boolean;
  $isDarkMode?: boolean;
}>`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  padding: 1px 2px;
  border-radius: 3px;
  cursor: text;
  line-height: 1.3;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  overflow: hidden;
  min-width: 0;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)"};
  }

  ${(props) =>
    props.$isLong
      ? `
    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 20px;
      background: linear-gradient(to right, transparent, ${(props: {
        $isDarkMode?: boolean;
      }) =>
        props.$isDarkMode !== false
          ? "rgba(30, 30, 30, 0.95)"
          : "rgba(255, 255, 255, 0.95)"});
      pointer-events: none;
      z-index: 1;
    }
  `
      : ""}
`;

const LayerNameText = styled.span<{ $isLong?: boolean }>`
  white-space: nowrap;
  display: inline-block;
  ${(props) =>
    props.$isLong
      ? `
    animation: scrollText 12s linear infinite;
    padding-right: 30px;
    
    @keyframes scrollText {
      0%, 25% {
        transform: translateX(0);
      }
      50%, 75% {
        transform: translateX(calc(-100% + 200px));
      }
      100% {
        transform: translateX(0);
      }
    }
  `
      : `
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  `}
`;

const LayerMetaInfo = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  color: ${(props) => (props.$isDarkMode ? "#888" : "#666")};
  flex-shrink: 0;
  margin-top: 0px;
  line-height: 1.2;
  transition: color 0.3s ease;
`;

const LayerTypeBadge = styled.div<{ $type: "state" | "guide" | "border" }>`
  padding: 2px 6px;
  border-radius: 3px;
  background: ${(props) =>
    props.$type === "guide"
      ? "rgba(33, 150, 243, 0.2)"
      : props.$type === "border"
      ? "rgba(239, 68, 68, 0.2)"
      : "rgba(76, 175, 80, 0.2)"};
  color: ${(props) => 
    props.$type === "guide" 
      ? "#2196f3" 
      : props.$type === "border"
      ? "#ef4444"
      : "#4caf50"};
  font-weight: 600;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const LayerControlButton = styled.button<{
  $active?: boolean;
  $danger?: boolean;
  $alwaysVisible?: boolean;
}>`
  width: 22px;
  height: 22px;
  border: ${(props) =>
    props.$alwaysVisible && !props.$active
      ? "1px solid rgba(0, 0, 0, 0.15)"
      : "none"};
  border-radius: 4px;
  background: ${(props) => {
    if (props.$alwaysVisible && !props.$active) {
      return "rgba(255, 255, 255, 0.8)";
    }
    if (!props.$active && !props.$danger) {
      return "rgba(0, 0, 0, 0.05)";
    }
    return "transparent";
  }};
  color: ${(props) => {
    if (props.$alwaysVisible && !props.$active) {
      return "#444";
    }
    return props.$danger ? "#f44336" : props.$active ? "#2196f3" : "#666";
  }};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
  padding: 0;

  &:hover {
    background: ${(props) => {
      if (props.$alwaysVisible) {
        return props.$active
          ? "rgba(33, 150, 243, 0.15)"
          : "rgba(0, 0, 0, 0.12)";
      }
      return props.$danger
        ? "rgba(244, 67, 54, 0.15)"
        : props.$active
        ? "rgba(33, 150, 243, 0.15)"
        : "rgba(0, 0, 0, 0.08)";
    }};
    color: ${(props) => {
      if (props.$alwaysVisible && !props.$active) {
        return "#000";
      }
      return props.$danger ? "#ff6b6b" : props.$active ? "#42a5f5" : "#333";
    }};
    border-color: ${(props) =>
      props.$alwaysVisible && !props.$active
        ? "rgba(0, 0, 0, 0.25)"
        : "transparent"};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const LayerOpacityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding: 6px 0;
`;

const LayerOpacitySlider = styled.input`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #2d2d2d;
  outline: none;
  accent-color: #2196f3;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #2196f3;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #2196f3;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const LayerOpacityValue = styled.div`
  font-size: 10px;
  color: #888;
  min-width: 32px;
  text-align: right;
  font-weight: 500;
`;

// Sticky Save Button
const StickyFooter = styled.div<{ $isDarkMode?: boolean }>`
  position: sticky;
  bottom: 0;
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#f5f5f5")};
  border-top: 1px solid
    ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  padding: 16px 20px;
  z-index: 100;
  box-shadow: 0 -4px 12px ${(props) => (props.$isDarkMode ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.1)")};
  transition: background 0.3s ease, border-color 0.3s ease;
`;

const SaveButtonError = styled.div<{ $isDarkMode?: boolean }>`
  margin-bottom: 12px;
  padding: 10px 14px;
  background: ${(props) =>
    props.$isDarkMode ? "rgba(244, 67, 54, 0.15)" : "rgba(244, 67, 54, 0.1)"};
  border: 1px solid
    ${(props) =>
      props.$isDarkMode ? "rgba(244, 67, 54, 0.4)" : "rgba(244, 67, 54, 0.3)"};
  border-radius: 6px;
  color: ${(props) => (props.$isDarkMode ? "#ff6b6b" : "#d32f2f")};
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const SaveButton = styled.button<{ $hasChanges?: boolean }>`
  width: 100%;
  padding: 14px;
  background: ${(props) => (props.$hasChanges ? "#2196f3" : "#404040")};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover:not(:disabled) {
    background: ${(props) => (props.$hasChanges ? "#1976d2" : "#505050")};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Empty State
const EmptyState = styled.div<{ $isDarkMode: boolean }>`
  text-align: center;
  padding: 60px 20px;
  color: ${(props) => (props.$isDarkMode ? "#888" : "#666")};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  transition: color 0.3s ease;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 8px;
  opacity: 0.5;
`;

const EmptyStateText = styled.div<{ $isDarkMode: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#666")};
  margin-bottom: 4px;
  transition: color 0.3s ease;
`;

const EmptyStateSubtext = styled.div<{ $isDarkMode?: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.$isDarkMode ? "#666" : "#999")};
  margin-bottom: 8px;
  transition: color 0.3s ease;
`;

const EmptyStateActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 280px;
  margin-top: 8px;
`;

const EmptyStateButton = styled.button<{ $isDarkMode: boolean }>`
  padding: 12px 20px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#404040" : "#d0d0d0")};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: transparent;
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  width: 100%;

  &:hover {
    background: ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#f0f0f0")};
    border-color: ${(props) => (props.$isDarkMode ? "#505050" : "#b0b0b0")};
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  }

  &:first-child {
    background: #2196f3;
    border-color: #2196f3;
    color: white;

    &:hover {
      background: #1976d2;
      border-color: #1976d2;
    }
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

// Toggle Switch
const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
`;

const ToggleLabel = styled.label<{ $isDarkMode: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  cursor: pointer;
  flex: 1;
  transition: color 0.3s ease;
`;

const ToggleSwitch = styled.label<{ $checked: boolean; $isDarkMode?: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  background: ${(props) =>
    props.$checked ? "#2196f3" : props.$isDarkMode ? "#404040" : "#ccc"};
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: ${(props) => (props.$checked ? "23px" : "3px")};
    transition: left 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const ToggleInput = styled.input`
  display: none;
`;

// Color Picker
const ColorPickerContainer = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: ${(props) => (props.$isDarkMode ? "#1e1e1e" : "#ffffff")};
  border-radius: 8px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#d0d0d0")};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.$isDarkMode ? "#404040" : "#b0b0b0")};
  }

  &:focus-within {
    border-color: #2196f3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
  }
`;

const ColorPicker = styled.input<{ $isDarkMode?: boolean }>`
  width: 56px;
  height: 56px;
  border: 2px solid ${(props) => (props.$isDarkMode ? "#404040" : "#d0d0d0")};
  border-radius: 8px;
  cursor: pointer;
  background: none;
  padding: 0;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.$isDarkMode ? "#505050" : "#b0b0b0")};
    transform: scale(1.05);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
`;

const ColorInput = styled(Input)`
  font-family: "Monaco", "Menlo", "Courier New", monospace;
  font-size: 13px;
  letter-spacing: 0.5px;
`;

const CapitalLocationButton = styled(IconButton)<{ $active?: boolean }>`
  position: relative;
  min-width: 44px;
  height: 44px;

  ${(props) =>
    props.$active &&
    `
    background: #2196f3;
    color: white;
    animation: pulse 2s infinite;
    
    @keyframes pulse {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
      }
      50% {
        box-shadow: 0 0 0 8px rgba(33, 150, 243, 0);
      }
    }
  `}
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #252525;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  justify-content: space-between;
`;

const ActionButtonsGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DeleteButton = styled.button`
  flex-shrink: 0;
  padding: 0 10px;
  border: 1px solid #f44336;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: transparent;
  color: #f44336;
  height: 28px;
  box-sizing: border-box;
  white-space: nowrap;
  margin-left: auto;

  &:hover {
    background: rgba(244, 67, 54, 0.15);
    border-color: #f44336;
    color: #ff6b6b;
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    display: block;
  }
`;

// Status Badge for Map Visibility
const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatusBadge = styled.div<{
  $variant?: "success" | "warning" | "danger" | "info" | "default";
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${(props) => {
    switch (props.$variant) {
      case "success":
        return "rgba(76, 175, 80, 0.15)";
      case "warning":
        return "rgba(255, 193, 7, 0.15)";
      case "danger":
        return "rgba(244, 67, 54, 0.15)";
      case "info":
        return "rgba(33, 150, 243, 0.15)";
      default:
        return "rgba(128, 128, 128, 0.15)";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$variant) {
        case "success":
          return "rgba(76, 175, 80, 0.3)";
        case "warning":
          return "rgba(255, 193, 7, 0.3)";
        case "danger":
          return "rgba(244, 67, 54, 0.3)";
        case "info":
          return "rgba(33, 150, 243, 0.3)";
        default:
          return "rgba(128, 128, 128, 0.3)";
      }
    }};
`;

const StatusIcon = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const StatusText = styled.span<{ $isDarkMode?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
`;

// Version History Styled Components
const LoadingState = styled.div<{ $isDarkMode: boolean }>`
  padding: 48px 24px;
  text-align: center;
  color: ${(props) => (props.$isDarkMode ? "#666" : "#999")};
`;

const VersionTimeline = styled.div<{ $isDarkMode: boolean }>`
  position: relative;
  padding: 4px 0;
`;

const VersionItem = styled.div<{
  $isDarkMode: boolean;
  $isCurrent: boolean;
  $isLast: boolean;
}>`
  position: relative;
  padding-left: 20px;
  padding-bottom: ${(props) => (props.$isLast ? "0" : "8px")};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &::before {
    content: "";
    position: absolute;
    left: 7px;
    top: 6px;
    width: 1px;
    height: ${(props) => (props.$isLast ? "6px" : "100%")};
    background: ${(props) => (props.$isDarkMode ? "#333" : "#e0e0e0")};
    transition: background 0.2s ease;
  }

  &::after {
    content: "";
    position: absolute;
    left: 3px;
    top: 3px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) =>
      props.$isCurrent ? "#4caf50" : props.$isDarkMode ? "#555" : "#ccc"};
    border: 1.5px solid
      ${(props) =>
        props.$isCurrent
          ? "#4caf50"
          : props.$isDarkMode
          ? "#2d2d2d"
          : "#ffffff"};
    transition: all 0.2s ease;
    z-index: 2;
    cursor: pointer;
  }

  &:hover::after {
    transform: scale(1.3);
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.$isCurrent
          ? "rgba(76, 175, 80, 0.3)"
          : props.$isDarkMode
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)"};
  }
`;

const VersionCard = styled.div<{
  $isDarkMode: boolean;
  $isCurrent: boolean;
}>`
  background: ${(props) =>
    props.$isCurrent
      ? props.$isDarkMode
        ? "rgba(76, 175, 80, 0.08)"
        : "rgba(76, 175, 80, 0.04)"
      : props.$isDarkMode
      ? "#1e1e1e"
      : "#ffffff"};
  border: 1px solid
    ${(props) =>
      props.$isCurrent ? "#4caf50" : props.$isDarkMode ? "#2d2d2d" : "#e0e0e0"};
  border-radius: 4px;
  padding: 8px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${(props) =>
      props.$isCurrent ? "#4caf50" : props.$isDarkMode ? "#404040" : "#b0b0b0"};
    box-shadow: 0 1px 4px
      ${(props) =>
        props.$isDarkMode ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.1)"};
  }
`;

const VersionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  gap: 6px;
`;

const VersionTitle = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const VersionNumber = styled.div<{ $isDarkMode: boolean; $isCurrent: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) =>
    props.$isCurrent ? "#4caf50" : props.$isDarkMode ? "#ffffff" : "#1a1a1a"};
  letter-spacing: -0.2px;
`;

const VersionBadges = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const VersionBadge = styled.span<{ $variant: "current" | "approved" | "info" }>`
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  background: ${(props) => {
    if (props.$variant === "current") return "#4caf50";
    if (props.$variant === "approved") return "#2196f3";
    return "#9c27b0"; // info/viewing
  }};
  color: white;
`;

const VersionMeta = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  flex-wrap: wrap;
`;

const VersionTime = styled.div<{ $isDarkMode: boolean }>`
  font-size: 10px;
  font-weight: 500;
  color: ${(props) => (props.$isDarkMode ? "#888" : "#666")};
  display: flex;
  align-items: center;
  gap: 3px;
`;

const VersionAuthor = styled.div<{ $isDarkMode: boolean }>`
  font-size: 10px;
  color: ${(props) => (props.$isDarkMode ? "#666" : "#999")};
  display: flex;
  align-items: center;
  gap: 3px;
`;

const VersionDescription = styled.div<{ $isDarkMode: boolean }>`
  font-size: 9px;
  color: ${(props) => (props.$isDarkMode ? "#aaa" : "#666")};
  font-style: italic;
  margin-top: 3px;
  padding: 3px 6px;
  background: ${(props) =>
    props.$isDarkMode ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)"};
  border-radius: 2px;
  border-left: 2px solid
    ${(props) => (props.$isDarkMode ? "#667eea" : "#764ba2")};
`;

const DiffSection = styled.div<{ $isDarkMode: boolean }>`
  margin-top: 4px;
  padding: 4px 6px;
  background: ${(props) =>
    props.$isDarkMode ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)"};
  border-radius: 3px;
  border: 1px solid ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
`;

const DiffTitle = styled.div<{ $isDarkMode: boolean }>`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${(props) => (props.$isDarkMode ? "#888" : "#666")};
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 3px;
`;

const DiffItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const DiffItem = styled.span<{ $type: "add" | "remove" | "modify" }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$type) {
      case "add":
        return "rgba(76, 175, 80, 0.15)";
      case "remove":
        return "rgba(244, 67, 54, 0.15)";
      case "modify":
        return "rgba(255, 152, 0, 0.15)";
    }
  }};
  color: ${(props) => {
    switch (props.$type) {
      case "add":
        return "#4caf50";
      case "remove":
        return "#f44336";
      case "modify":
        return "#ff9800";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$type) {
        case "add":
          return "rgba(76, 175, 80, 0.3)";
        case "remove":
          return "rgba(244, 67, 54, 0.3)";
        case "modify":
          return "rgba(255, 152, 0, 0.3)";
      }
    }};
`;

const StatePanel = forwardRef<any, StatePanelProps>(
  (
    {
      states,
      selectedState,
      onStateSelect,
      onStateUpdate,
      onStateDelete,
      onAddPredefinedState,
      onTemplateSelect,
      editingStateId,
      setEditingStateId,
      drawingMode,
      setDrawingMode,
      onSaveMap,
      onExportTemplate,
      onImportTemplate,
      onClearAllStates,
      guideLayers,
      onAddGuideLayer,
      onUpdateGuideLayer,
      onDeleteGuideLayer,
      onToggleGuideLayer,
      mapName,
      setMapName,
      mapDescription,
      setMapDescription,
      mapRegion,
      onRegionChange,
      isPublic,
      setIsPublic,
      showCapitals,
      setShowCapitals,
      showCapitalNames,
      setShowCapitalNames,
      showStateNames,
      setShowStateNames,
      showStateFill,
      setShowStateFill,
      mapStyle,
      setMapStyle,
      isEditMode,
      isSettingCapital,
      setIsSettingCapital,
      onCapitalCoordinateSet,
      setEditingGuideLayerId,
      mapRef,
      currentThumbnail,
      onCustomThumbnailFile,
      mapId,
      approvalRequested,
      isApproved,
      isRejected,
      rejectionReason,
      version,
      approvedVersion,
      onRestoreVersion,
      showBorder,
      setShowBorder,
      customBorder,
      isEditingBorder,
      onStartEditingBorder,
      onFinishEditingBorder,
      onDeleteBorder,
    },
    ref
  ) => {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();

    const canCreatePublicMaps =
      user?.role === "admin" || user?.role === "approver";
    let canMakePublic = canCreatePublicMaps;
    if (isEditMode) {
      canMakePublic = canCreatePublicMaps;
    }

    // Local state declarations
    const [isTemplateManagerOpen, setIsTemplateManagerOpen] = useState(false);
    const [showGettingStarted, setShowGettingStarted] = useState(() => {
      if (user && user.mapsCount === 0 && !isEditMode && states.length === 0) {
        const dismissed = localStorage.getItem(
          "mapEditorGettingStartedDismissed"
        );
        return !dismissed;
      }
      return false;
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [tempState, setTempState] = useState<State | null>(null);
    const [showAddGuideLayerForm, setShowAddGuideLayerForm] = useState(false);
    const [selectedGuideLayer, setSelectedGuideLayer] =
      useState<GuideLayer | null>(null);
    const [editingLayerName, setEditingLayerName] = useState<string | null>(
      null
    );
    // Smart auto-expand: expand states if <= 10, collapse if > 10
    // Always expand guide layers (usually 1-5)
    const [expandedGroups, setExpandedGroups] = useState({
      states: states.length <= 10,
      guides: true,
    });
    // Version history state
    const [versions, setVersions] = useState<MapVersion[]>([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [loadedMapId, setLoadedMapId] = useState<string | null>(null);
    const [loadedVersion, setLoadedVersion] = useState<number | null>(null);
    const isLoadingRef = useRef(false);
    const lastLoadedVersionRef = useRef<number | null>(null);
    const previousVersionRef = useRef<number | undefined>(version);

    // Update expansion when states count changes significantly
    useEffect(() => {
      if (states.length > 10 && expandedGroups.states) {
        setExpandedGroups((prev) => ({ ...prev, states: false }));
      } else if (states.length <= 10 && !expandedGroups.states) {
        setExpandedGroups((prev) => ({ ...prev, states: true }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [states.length]);
    const [layerSearchQuery, setLayerSearchQuery] = useState("");
    const [showLayerSearch, setShowLayerSearch] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Navigation state - which tab is active (must be after selectedGuideLayer declaration)
    const [activeTab, setActiveTab] = useState<
      "map" | "layers" | "tools" | "settings" | "versions"
    >(selectedState || selectedGuideLayer ? "layers" : "map");
    const [newGuideLayer, setNewGuideLayer] = useState<
      Partial<GuideLayer> & { imageFile?: File }
    >({
      name: "",
      description: "",
      imageUrl: "",
      imageFile: undefined,
      opacity: 0.5,
      visible: true,
    });

    // Update active tab when selection changes - only switch to layers when something is selected
    // Don't automatically switch away from layers when selection is cleared
    useEffect(() => {
      if (selectedState || selectedGuideLayer) {
        setActiveTab("layers");
      }
      // Removed automatic switch to "map" tab when selection is cleared
      // This allows users to stay on the layers tab after saving/canceling
    }, [selectedState, selectedGuideLayer]);

    // Update tempState when selectedState changes
    useEffect(() => {
      if (selectedState) {
        setTempState({ ...selectedState });
      } else {
        setTempState(null);
      }
    }, [selectedState]);

    // Sync selectedGuideLayer with the latest guide layer data when editing
    useEffect(() => {
      if (selectedGuideLayer && guideLayers) {
        const updatedGuideLayer = guideLayers.find(
          (layer) => layer.id === selectedGuideLayer.id
        );
        if (updatedGuideLayer && updatedGuideLayer !== selectedGuideLayer) {
          setSelectedGuideLayer(updatedGuideLayer);
        }
      }
    }, [guideLayers, selectedGuideLayer]);

    // Track unsaved changes
    useEffect(() => {
      if (mapName || mapDescription || states.length > 0) {
        setHasUnsavedChanges(true);
      }
    }, [mapName, mapDescription, states.length]);

    // Hide getting started when first state is created
    useEffect(() => {
      if (states.length > 0 && showGettingStarted) {
        setShowGettingStarted(false);
        localStorage.setItem("mapEditorGettingStartedDismissed", "true");
      }
    }, [states.length, showGettingStarted]);

    const handleSaveMapClick = useCallback(() => {
      setHasUnsavedChanges(false);
      onSaveMap();
    }, [onSaveMap]);

    // Keyboard shortcut for save
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "s") {
          e.preventDefault();
          if (
            !selectedState &&
            !selectedGuideLayer &&
            mapName &&
            mapName.trim() !== "" &&
            states.length > 0
          ) {
            handleSaveMapClick();
          }
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [
      mapName,
      states.length,
      selectedState,
      selectedGuideLayer,
      handleSaveMapClick,
    ]);

    useImperativeHandle(ref, () => ({
      handleCapitalCoordinateSet: (
        stateId: string,
        coordinates: Coordinates
      ) => {
        if (tempState && tempState.id === stateId) {
          const updatedTempState = {
            ...tempState,
            capitalCoordinates: coordinates,
          };
          setTempState(updatedTempState);
        }
      },
      getTempState: () => tempState,
      getSelectedGuideLayer: () => selectedGuideLayer,
    }));

    const handleDismissGettingStarted = () => {
      setShowGettingStarted(false);
      localStorage.setItem("mapEditorGettingStartedDismissed", "true");
    };

    const handleStateClick = (state: State) => {
      setSelectedGuideLayer(null);
      setEditingGuideLayerId?.(null);
      onStateSelect(state);
      setActiveTab("layers");
    };

    const handleSave = () => {
      if (tempState) {
        try {
          onStateUpdate(tempState);
          toast.success("State changes saved successfully!");
          setIsSettingCapital?.(false);
          setTempState(null);
          onStateSelect(null);
          setSelectedGuideLayer(null);
          setEditingGuideLayerId?.(null);
        } catch (error) {
          toast.error("Failed to save state changes");
        }
      }
    };

    const handleCancel = () => {
      setIsSettingCapital?.(false);
      setTempState(null);
      onStateSelect(null);
      setSelectedGuideLayer(null);
      setEditingGuideLayerId?.(null);
    };

    const handleDelete = () => {
      if (selectedState) {
        onStateDelete(selectedState.id);
        onStateSelect(null);
      }
    };

    const handleToggleDrawing = () => {
      setDrawingMode(!drawingMode);
    };

    const updateTempState = (updates: Partial<State>) => {
      if (tempState) {
        setTempState({ ...tempState, ...updates });
      }
    };

    const handleGuideLayerClick = (guideLayer: GuideLayer) => {
      onStateSelect(null);
      setTempState(null);
      setSelectedGuideLayer(guideLayer);
      setEditingGuideLayerId?.(guideLayer.id);
      setActiveTab("layers");
    };

    const handleLayerNameEdit = (layerId: string, currentName: string) => {
      setEditingLayerName(layerId);
    };

    const handleLayerNameSave = (
      layerId: string,
      newName: string,
      type: "state" | "guide"
    ) => {
      if (newName.trim() === "") {
        toast.error("Layer name cannot be empty");
        return;
      }

      if (type === "state") {
        const state = states.find((s) => s.id === layerId);
        if (state) {
          onStateUpdate({ ...state, name: newName.trim() });
        }
      } else {
        const guideLayer = guideLayers?.find((g) => g.id === layerId);
        if (guideLayer) {
          onUpdateGuideLayer?.(layerId, { name: newName.trim() });
        }
      }

      setEditingLayerName(null);
    };

    const handleLayerNameKeyDown = (
      e: React.KeyboardEvent,
      layerId: string,
      currentName: string,
      type: "state" | "guide"
    ) => {
      if (e.key === "Enter") {
        handleLayerNameSave(layerId, currentName, type);
      } else if (e.key === "Escape") {
        setEditingLayerName(null);
      }
    };

    const toggleGroup = (group: "states" | "guides") => {
      setExpandedGroups((prev) => ({
        ...prev,
        [group]: !prev[group],
      }));
    };

    // Render Map Tab Content
    const renderMapTab = () => (
      <ContentSection $active={activeTab === "map"}>
        {showGettingStarted && !selectedState && !selectedGuideLayer && (
          <GettingStartedCard>
            <DismissButton
              onClick={handleDismissGettingStarted}
              title="Dismiss"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </DismissButton>
            <GettingStartedTitle>
              🎉 Let's create your first map!
            </GettingStartedTitle>
            <GettingStartedDescription>
              Start by drawing your first state on the map, or choose from
              templates to get started quickly.
            </GettingStartedDescription>
            <GettingStartedActions>
              <GettingStartedButton
                $primary
                onClick={() => {
                  handleToggleDrawing();
                  handleDismissGettingStarted();
                }}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 0 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z" />
                </svg>
                Start Drawing
              </GettingStartedButton>
              <GettingStartedButton
                onClick={() => {
                  setIsTemplateManagerOpen(true);
                  handleDismissGettingStarted();
                }}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3A1.5 1.5 0 0 1 15 10.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
                </svg>
                Browse Templates
              </GettingStartedButton>
              <GettingStartedButton
                onClick={() => {
                  document.getElementById("import-template-input")?.click();
                  handleDismissGettingStarted();
                }}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z" />
                </svg>
                Import from File
              </GettingStartedButton>
            </GettingStartedActions>
          </GettingStartedCard>
        )}

        {!selectedState &&
          !selectedGuideLayer &&
          states.length === 0 &&
          !showGettingStarted && (
            <Card $isDarkMode={isDarkMode}>
              <PrimaryButton
                $active={drawingMode}
                $fullWidth
                onClick={handleToggleDrawing}
              >
                {drawingMode ? (
                  <>
                    <svg viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                    </svg>
                    Drawing Mode Active
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 16 16" fill="currentColor">
                      <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 0 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z" />
                    </svg>
                    Start Drawing Your First State
                  </>
                )}
              </PrimaryButton>
            </Card>
          )}

        <Section $isDarkMode={isDarkMode}>
          <SectionHeader $isDarkMode={isDarkMode}>
            <SectionIcon>🗺️</SectionIcon>
            Map Details
          </SectionHeader>
          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Map Name</Label>
            <Input
              $isDarkMode={isDarkMode}
              type="text"
              value={mapName || ""}
              onChange={(e) => setMapName?.(e.target.value)}
              placeholder="Enter map name..."
            />
          </FormGroup>
          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Description</Label>
            <TextArea
              $isDarkMode={isDarkMode}
              value={mapDescription || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 1000) {
                  setMapDescription?.(value);
                }
              }}
              placeholder="Describe your map..."
              rows={4}
              maxLength={1000}
            />
            <FieldHint
              $isDarkMode={isDarkMode}
              style={{ marginTop: "4px", fontSize: "11px" }}
            >
              {mapDescription?.length || 0}/1000 characters
            </FieldHint>
          </FormGroup>
          <FormGroup>
            <Label $isDarkMode={isDarkMode}>Region</Label>
            {user?.role === "admin" ? (
              <select
                value={mapRegion || "iran"}
                onChange={(e) => onRegionChange?.(e.target.value as RegionCode)}
                style={{
                  width: "100%",
                  padding: "0.5rem 0.75rem",
                  background: isDarkMode ? "#2d3748" : "#fff",
                  border: `1px solid ${isDarkMode ? "#4a5568" : "#e2e8f0"}`,
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  color: isDarkMode ? "#e2e8f0" : "#4a5568",
                  cursor: "pointer",
                }}
              >
                <option value="iran">🌍 Iran</option>
                <option value="iraq">🌍 Iraq</option>
                <option value="syria">🌍 Syria</option>
                <option value="kurdistan">🌍 Kurdistan</option>
                <option value="afghanistan">🌍 Afghanistan</option>
                <option value="baluchistan">🌍 Baluchistan</option>
              </select>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 0.75rem",
                  background: isDarkMode ? "#2d3748" : "#e2e8f0",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  color: isDarkMode ? "#e2e8f0" : "#4a5568",
                }}
              >
                <span>🌍</span>
                <span>{mapRegion ? mapRegion.charAt(0).toUpperCase() + mapRegion.slice(1) : "Iran"}</span>
              </div>
            )}
          </FormGroup>
        </Section>

        <Section $isDarkMode={isDarkMode}>
          <SectionHeader $isDarkMode={isDarkMode}>
            <SectionIcon>🖼️</SectionIcon>
            Thumbnail
          </SectionHeader>
          {currentThumbnail && currentThumbnail.trim() !== "" ? (
            <div
              style={{
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #2d2d2d",
                cursor: "pointer",
                position: "relative",
              }}
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) onCustomThumbnailFile?.(file);
                };
                input.click();
              }}
            >
              <img
                src={currentThumbnail}
                alt="Map thumbnail"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
              >
                <span style={{ color: "white", fontWeight: "600" }}>
                  Click to replace
                </span>
              </div>
            </div>
          ) : (
            <div
              style={{
                border: `2px dashed ${isDarkMode ? "#404040" : "#d0d0d0"}`,
                borderRadius: "8px",
                padding: "40px 20px",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) onCustomThumbnailFile?.(file);
                };
                input.click();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#2196f3";
                e.currentTarget.style.background = isDarkMode
                  ? "#252525"
                  : "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isDarkMode
                  ? "#404040"
                  : "#d0d0d0";
                e.currentTarget.style.background = "transparent";
              }}
            >
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>📷</div>
              <div
                style={{
                  color: "#b0b0b0",
                  fontSize: "14px",
                  marginBottom: "4px",
                }}
              >
                Click to upload thumbnail
              </div>
              <div
                style={{
                  color: isDarkMode ? "#666" : "#999",
                  fontSize: "12px",
                }}
              >
                JPG, PNG • Max 5MB
              </div>
            </div>
          )}
        </Section>
      </ContentSection>
    );

    // Helper function to format numbers
    const formatNumber = (num: number | undefined): string => {
      if (!num) return "";
      return num.toLocaleString();
    };

    // Helper function to parse formatted number
    const parseFormattedNumber = (str: string): number | undefined => {
      const cleaned = str.replace(/,/g, "");
      const parsed = parseInt(cleaned, 10);
      return isNaN(parsed) ? undefined : parsed;
    };

    // Render Layers Tab Content
    const renderLayersTab = () => (
      <ContentSection $active={activeTab === "layers"}>
        {tempState && (
          <Card $isDarkMode={isDarkMode}>
            <CardHeader>
              <CardTitle $isDarkMode={isDarkMode}>
                <LayerThumbnail $color={tempState.color} $type="state" />
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                >
                  Edit State
                </div>
              </CardTitle>
            </CardHeader>

            <FormSection $isDarkMode={isDarkMode}>
              <FormSectionTitle $isDarkMode={isDarkMode}>
                Basic Information
              </FormSectionTitle>

              <FormGroup>
                <Label $isDarkMode={isDarkMode}>
                  <LabelText>State Name</LabelText>
                  <FieldHint>Required</FieldHint>
                </Label>
                <Input
                  $isDarkMode={isDarkMode}
                  type="text"
                  value={tempState.name || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 100) {
                      updateTempState({ name: value });
                    }
                  }}
                  placeholder="Enter state name..."
                  maxLength={100}
                />
                <FieldHint
                  $isDarkMode={isDarkMode}
                  style={{ marginTop: "4px", fontSize: "11px" }}
                >
                  {tempState.name?.length || 0}/100 characters
                </FieldHint>
              </FormGroup>

              <FormGroup>
                <Label $isDarkMode={isDarkMode}>
                  <LabelText>Description</LabelText>
                  <FieldHint>Optional</FieldHint>
                </Label>
                <TextArea
                  $isDarkMode={isDarkMode}
                  value={tempState.description || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 500) {
                      updateTempState({ description: value });
                    }
                  }}
                  placeholder="Enter a description for this state..."
                  rows={6}
                  maxLength={500}
                />
                <FieldHint
                  $isDarkMode={isDarkMode}
                  style={{ marginTop: "4px", fontSize: "11px" }}
                >
                  {tempState.description?.length || 0}/500 characters
                </FieldHint>
              </FormGroup>
            </FormSection>

            <FormSection $isDarkMode={isDarkMode}>
              <FormSectionTitle $isDarkMode={isDarkMode}>
                Location & Demographics
              </FormSectionTitle>

              <FormGroup>
                <Label $isDarkMode={isDarkMode}>
                  <LabelText>Capital City</LabelText>
                  <FieldHint>Optional</FieldHint>
                </Label>
                <InputGroup>
                  <Input
                    $isDarkMode={isDarkMode}
                    type="text"
                    value={tempState.capital || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 50) {
                        updateTempState({ capital: value });
                      }
                    }}
                    placeholder="Enter capital city name..."
                    style={{ flex: 1 }}
                    maxLength={50}
                  />
                  <CapitalLocationButton
                    $variant={isSettingCapital ? "primary" : undefined}
                    $active={isSettingCapital || false}
                    onClick={() => setIsSettingCapital?.(!isSettingCapital)}
                    title={
                      isSettingCapital
                        ? "Click on map to set location (click again to cancel)"
                        : "Set capital location on map"
                    }
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
                    </svg>
                  </CapitalLocationButton>
                </InputGroup>
                <FieldHint
                  $isDarkMode={isDarkMode}
                  style={{ marginTop: "4px", fontSize: "11px" }}
                >
                  {tempState.capital?.length || 0}/50 characters
                </FieldHint>
                {isSettingCapital && (
                  <FieldHint
                    $isDarkMode={isDarkMode}
                    style={{
                      marginTop: "6px",
                      color: "#2196f3",
                      display: "block",
                    }}
                  >
                    Click on the map to set the capital location
                  </FieldHint>
                )}
              </FormGroup>

              <FormGroup>
                <Label $isDarkMode={isDarkMode}>
                  <LabelText>Population</LabelText>
                  <FieldHint>Optional</FieldHint>
                </Label>
                <Input
                  $isDarkMode={isDarkMode}
                  type="text"
                  value={formatNumber(tempState.population)}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/,/g, "");
                    // Limit to 15 digits (max safe integer is 9,007,199,254,740,991)
                    if (cleaned.length <= 15 && /^\d*$/.test(cleaned)) {
                      const parsed = parseFormattedNumber(e.target.value);
                      updateTempState({ population: parsed });
                    }
                  }}
                  placeholder="Enter population (e.g., 1,000,000)"
                  maxLength={18}
                />
              </FormGroup>
            </FormSection>

            <FormSection $isDarkMode={isDarkMode}>
              <FormSectionTitle $isDarkMode={isDarkMode}>
                Visual Appearance
              </FormSectionTitle>

              <FormGroup>
                <Label $isDarkMode={isDarkMode}>
                  <LabelText>State Color</LabelText>
                  <FieldHint>Click to change</FieldHint>
                </Label>
                <ColorPickerContainer $isDarkMode={isDarkMode}>
                  <ColorPicker
                    $isDarkMode={isDarkMode}
                    type="color"
                    value={tempState.color || "#3388ff"}
                    onChange={(e) => updateTempState({ color: e.target.value })}
                    title="Click to pick a color"
                  />
                  <ColorInput
                    $isDarkMode={isDarkMode}
                    type="text"
                    value={tempState.color || "#3388ff"}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        updateTempState({ color: value });
                      }
                    }}
                    placeholder="#3388ff"
                    style={{ flex: 1 }}
                  />
                </ColorPickerContainer>
              </FormGroup>
            </FormSection>

            <ActionButtonsContainer>
              <ActionButtonsGroup>
                <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
                <SecondaryButton
                  $isDarkMode={isDarkMode}
                  onClick={handleCancel}
                >
                  Cancel
                </SecondaryButton>
              </ActionButtonsGroup>
              <DeleteButton onClick={handleDelete} title="Delete this state">
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                  <path
                    fillRule="evenodd"
                    d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                  />
                </svg>
                Delete
              </DeleteButton>
            </ActionButtonsContainer>
          </Card>
        )}

        {selectedGuideLayer && (
          <Card $isDarkMode={isDarkMode}>
            <CardHeader>
              <CardTitle $isDarkMode={isDarkMode}>
                <CardIcon>📐</CardIcon>
                Edit Guide Layer: {selectedGuideLayer.name}
              </CardTitle>
            </CardHeader>
            <FormGroup>
              <Label $isDarkMode={isDarkMode}>Name</Label>
              <Input
                $isDarkMode={isDarkMode}
                type="text"
                value={selectedGuideLayer.name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 100) {
                    setSelectedGuideLayer({
                      ...selectedGuideLayer,
                      name: value,
                    });
                  }
                }}
                maxLength={100}
              />
              <FieldHint
                $isDarkMode={isDarkMode}
                style={{ marginTop: "4px", fontSize: "11px" }}
              >
                {selectedGuideLayer.name.length}/100 characters
              </FieldHint>
            </FormGroup>
            <FormGroup>
              <Label $isDarkMode={isDarkMode}>Description</Label>
              <TextArea
                $isDarkMode={isDarkMode}
                value={selectedGuideLayer.description || ""}
                onChange={(e) =>
                  setSelectedGuideLayer({
                    ...selectedGuideLayer,
                    description: e.target.value,
                  })
                }
                rows={3}
              />
            </FormGroup>
            <FormGroup>
              <Label $isDarkMode={isDarkMode}>
                Opacity: {Math.round((selectedGuideLayer.opacity || 0.5) * 100)}
                %
              </Label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={selectedGuideLayer.opacity || 0.5}
                onChange={(e) => {
                  const newOpacity = parseFloat(e.target.value);
                  setSelectedGuideLayer({
                    ...selectedGuideLayer,
                    opacity: newOpacity,
                  });
                  onUpdateGuideLayer?.(selectedGuideLayer.id, {
                    opacity: newOpacity,
                  });
                }}
                style={{
                  width: "100%",
                  accentColor: "#2196f3",
                  height: "6px",
                  borderRadius: "3px",
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label $isDarkMode={isDarkMode}>Image</Label>
              {selectedGuideLayer.imageUrl && (
                <div
                  style={{
                    marginBottom: "12px",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #2d2d2d",
                  }}
                >
                  <img
                    src={selectedGuideLayer.imageUrl}
                    alt="Guide layer"
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
              <SecondaryButton
                $isDarkMode={isDarkMode}
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  input.onchange = async (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      try {
                        const loadingToast =
                          toast.loading("Uploading image...");
                        const firebaseUrl = await uploadFileToFirebase(file);
                        setSelectedGuideLayer({
                          ...selectedGuideLayer,
                          imageUrl: firebaseUrl,
                        });
                        onUpdateGuideLayer?.(selectedGuideLayer.id, {
                          imageUrl: firebaseUrl,
                        });
                        toast.dismiss(loadingToast);
                        toast.success("Image updated successfully!");
                      } catch (error) {
                        toast.error("Failed to upload image");
                      }
                    }
                  };
                  input.click();
                }}
                style={{ width: "100%" }}
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                  <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                </svg>
                Replace Image
              </SecondaryButton>
            </FormGroup>
            <ToggleContainer>
              <ToggleLabel $isDarkMode={isDarkMode} htmlFor="guide-visible">
                Visible on map
              </ToggleLabel>
              <ToggleSwitch
                $isDarkMode={isDarkMode}
                $checked={selectedGuideLayer.visible}
                htmlFor="guide-visible"
              >
                <ToggleInput
                  type="checkbox"
                  id="guide-visible"
                  checked={selectedGuideLayer.visible}
                  onChange={(e) => {
                    const newVisible = e.target.checked;
                    setSelectedGuideLayer({
                      ...selectedGuideLayer,
                      visible: newVisible,
                    });
                    onUpdateGuideLayer?.(selectedGuideLayer.id, {
                      visible: newVisible,
                    });
                  }}
                />
              </ToggleSwitch>
            </ToggleContainer>
            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <PrimaryButton
                onClick={() => {
                  if (selectedGuideLayer) {
                    onUpdateGuideLayer?.(
                      selectedGuideLayer.id,
                      selectedGuideLayer
                    );
                    toast.success("Guide layer updated!");
                    setSelectedGuideLayer(null);
                    setEditingGuideLayerId?.(null);
                  }
                }}
                style={{ flex: 1 }}
              >
                Save Changes
              </PrimaryButton>
              <SecondaryButton
                $isDarkMode={isDarkMode}
                onClick={() => {
                  setSelectedGuideLayer(null);
                  setEditingGuideLayerId?.(null);
                }}
              >
                Cancel
              </SecondaryButton>
              <IconButton
                $variant="danger"
                onClick={() => {
                  if (selectedGuideLayer) {
                    onDeleteGuideLayer?.(selectedGuideLayer.id);
                    setSelectedGuideLayer(null);
                    setEditingGuideLayerId?.(null);
                  }
                }}
                title="Delete guide layer"
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                  <path
                    fillRule="evenodd"
                    d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                  />
                </svg>
              </IconButton>
            </div>
          </Card>
        )}

        {!tempState && !selectedGuideLayer && (
          <>
            {showAddGuideLayerForm && (
              <Card
                $isDarkMode={isDarkMode}
                style={{ marginBottom: "16px", borderColor: "#2196f3" }}
              >
                <CardHeader>
                  <CardTitle $isDarkMode={isDarkMode}>
                    <CardIcon>📐</CardIcon>
                    Add Guide Layer
                  </CardTitle>
                </CardHeader>
                <FormGroup>
                  <Label $isDarkMode={isDarkMode}>Name</Label>
                  <Input
                    $isDarkMode={isDarkMode}
                    type="text"
                    value={newGuideLayer.name || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 100) {
                        setNewGuideLayer({
                          ...newGuideLayer,
                          name: value,
                        });
                      }
                    }}
                    placeholder="Enter guide layer name..."
                    maxLength={100}
                  />
                  <FieldHint
                    $isDarkMode={isDarkMode}
                    style={{ marginTop: "4px", fontSize: "11px" }}
                  >
                    {newGuideLayer.name?.length || 0}/100 characters
                  </FieldHint>
                </FormGroup>
                <FormGroup>
                  <Label $isDarkMode={isDarkMode}>Description</Label>
                  <TextArea
                    $isDarkMode={isDarkMode}
                    value={newGuideLayer.description || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 500) {
                        setNewGuideLayer({
                          ...newGuideLayer,
                          description: value,
                        });
                      }
                    }}
                    placeholder="Enter description (optional)..."
                    rows={2}
                    maxLength={500}
                  />
                  <FieldHint
                    $isDarkMode={isDarkMode}
                    style={{ marginTop: "4px", fontSize: "11px" }}
                  >
                    {newGuideLayer.description?.length || 0}/500 characters
                  </FieldHint>
                </FormGroup>
                <FormGroup>
                  <Label $isDarkMode={isDarkMode}>Image File</Label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const tempUrl = URL.createObjectURL(file);
                        setNewGuideLayer({
                          ...newGuideLayer,
                          imageFile: file,
                          imageUrl: tempUrl,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: isDarkMode
                        ? "1px solid #2d2d2d"
                        : "1px solid #d1d5db",
                      borderRadius: "8px",
                      background: isDarkMode ? "#1e1e1e" : "#f9fafb",
                      color: isDarkMode ? "#e0e0e0" : "#374151",
                      fontSize: "14px",
                    }}
                  />
                </FormGroup>
                <FormGroup>
                  <Label $isDarkMode={isDarkMode}>
                    Opacity: {Math.round((newGuideLayer.opacity || 0.5) * 100)}%
                  </Label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={newGuideLayer.opacity || 0.5}
                    onChange={(e) =>
                      setNewGuideLayer({
                        ...newGuideLayer,
                        opacity: parseFloat(e.target.value),
                      })
                    }
                    style={{
                      width: "100%",
                      accentColor: "#2196f3",
                      height: "6px",
                    }}
                  />
                </FormGroup>
                <div
                  style={{ display: "flex", gap: "10px", marginTop: "20px" }}
                >
                  <PrimaryButton
                    onClick={async () => {
                      if (!newGuideLayer.name || !newGuideLayer.imageFile) {
                        toast.error("Please provide both name and image file");
                        return;
                      }

                      const loadingToast = toast.loading("Uploading image...");

                      try {
                        const firebaseUrl = await uploadFileToFirebase(
                          newGuideLayer.imageFile!
                        );

                        const calculateBoundsFromImage = (
                          file: File
                        ): Promise<{
                          bounds: Coordinates[];
                          centerLat: number;
                          centerLng: number;
                          width: number;
                          height: number;
                        }> => {
                          return new Promise((resolve) => {
                            const img = new Image();
                            img.onload = () => {
                              const aspectRatio = img.width / img.height;
                              const baseWidth = 20;
                              const baseHeight = baseWidth / aspectRatio;
                              const centerLat = 32.5;
                              const centerLng = 54;
                              const halfWidth = baseWidth / 2;
                              const halfHeight = baseHeight / 2;

                              const bounds = [
                                {
                                  lat: centerLat - halfHeight,
                                  lng: centerLng - halfWidth,
                                },
                                {
                                  lat: centerLat - halfHeight,
                                  lng: centerLng + halfWidth,
                                },
                                {
                                  lat: centerLat + halfHeight,
                                  lng: centerLng + halfWidth,
                                },
                                {
                                  lat: centerLat + halfHeight,
                                  lng: centerLng - halfWidth,
                                },
                              ];

                              resolve({
                                bounds,
                                centerLat,
                                centerLng,
                                width: baseWidth,
                                height: baseHeight,
                              });
                            };
                            img.src = URL.createObjectURL(file);
                          });
                        };

                        const { bounds, centerLat, centerLng, width, height } =
                          await calculateBoundsFromImage(
                            newGuideLayer.imageFile!
                          );

                        const guideLayerToAdd: GuideLayer = {
                          id: `guide-${Date.now()}`,
                          name: newGuideLayer.name || "",
                          description: newGuideLayer.description || "",
                          imageUrl: firebaseUrl,
                          bounds,
                          opacity: newGuideLayer.opacity || 0.5,
                          visible: newGuideLayer.visible || true,
                          rotation: 0,
                          scale: 1,
                          offsetX: 0,
                          offsetY: 0,
                          centerLat,
                          centerLng,
                          width,
                          height,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                        };

                        onAddGuideLayer?.(guideLayerToAdd);
                        setNewGuideLayer({
                          name: "",
                          description: "",
                          imageUrl: "",
                          imageFile: undefined,
                          opacity: 0.5,
                          visible: true,
                        });
                        setShowAddGuideLayerForm(false);

                        toast.dismiss(loadingToast);
                        toast.success("Guide layer added successfully!");
                      } catch (error) {
                        console.error("Error adding guide layer:", error);
                        toast.dismiss(loadingToast);
                        toast.error(
                          `Failed to add guide layer: ${
                            error instanceof Error
                              ? error.message
                              : "Unknown error"
                          }`
                        );
                      }
                    }}
                    disabled={!newGuideLayer.name || !newGuideLayer.imageFile}
                    style={{ flex: 1 }}
                  >
                    Add Guide Layer
                  </PrimaryButton>
                  <SecondaryButton
                    $isDarkMode={isDarkMode}
                    onClick={() => {
                      setShowAddGuideLayerForm(false);
                      setNewGuideLayer({
                        name: "",
                        description: "",
                        imageUrl: "",
                        imageFile: undefined,
                        opacity: 0.5,
                        visible: true,
                      });
                    }}
                  >
                    Cancel
                  </SecondaryButton>
                </div>
              </Card>
            )}

            {states.length === 0 && (guideLayers?.length || 0) === 0 ? (
              <EmptyState $isDarkMode={isDarkMode}>
                <EmptyStateIcon>📋</EmptyStateIcon>
                <EmptyStateText $isDarkMode={isDarkMode}>
                  No layers yet
                </EmptyStateText>
                <EmptyStateSubtext $isDarkMode={isDarkMode}>
                  Start by drawing a state or adding a template
                </EmptyStateSubtext>
                <EmptyStateActions>
                  <EmptyStateButton
                    $isDarkMode={isDarkMode}
                    onClick={handleToggleDrawing}
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor">
                      <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 0 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z" />
                    </svg>
                    Start Drawing
                  </EmptyStateButton>
                  <EmptyStateButton
                    $isDarkMode={isDarkMode}
                    onClick={() => setIsTemplateManagerOpen(true)}
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor">
                      <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3A1.5 1.5 0 0 1 15 10.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
                    </svg>
                    Browse Templates
                  </EmptyStateButton>
                </EmptyStateActions>
              </EmptyState>
            ) : (
              <LayerPanel>
                <LayerPanelHeader $isDarkMode={isDarkMode}>
                  <LayerPanelHeaderTop>
                    <LayerPanelTitle $isDarkMode={isDarkMode}>
                      Layers ({states.length + (guideLayers?.length || 0)})
                      {states.length > 20 && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "10px",
                            color: "#888",
                            fontWeight: "normal",
                          }}
                          title="Use search to quickly find layers"
                        >
                          (Use search for quick access)
                        </span>
                      )}
                    </LayerPanelTitle>
                    <LayerPanelActions>
                      <LayerPanelActionButton
                        $isDarkMode={isDarkMode}
                        title="Search layers"
                        onClick={() => {
                          const newState = !showLayerSearch;
                          setShowLayerSearch(newState);
                          if (newState) {
                            setTimeout(() => {
                              searchInputRef.current?.focus();
                            }, 100);
                          } else {
                            setLayerSearchQuery("");
                          }
                        }}
                        style={{
                          background: showLayerSearch
                            ? "#2196f3"
                            : "transparent",
                          color: showLayerSearch ? "#ffffff" : "#b0b0b0",
                        }}
                      >
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                        </svg>
                      </LayerPanelActionButton>
                      <LayerPanelActionButton
                        $isDarkMode={isDarkMode}
                        title="Browse Templates"
                        onClick={() => {
                          setIsTemplateManagerOpen(true);
                        }}
                      >
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3A1.5 1.5 0 0 1 15 10.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
                        </svg>
                      </LayerPanelActionButton>
                    </LayerPanelActions>
                  </LayerPanelHeaderTop>
                  <LayerSearchContainer $visible={showLayerSearch}>
                    <LayerSearchInput
                      $isDarkMode={isDarkMode}
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search layers..."
                      value={layerSearchQuery}
                      onChange={(e) => setLayerSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={() => {
                        if (layerSearchQuery === "") {
                          setShowLayerSearch(false);
                        }
                      }}
                    />
                  </LayerSearchContainer>
                </LayerPanelHeader>

                {/* States Group - Primary Section */}
                <LayerGroup $isPrimary>
                  <LayerGroupHeader
                    $isPrimary
                    $isDarkMode={isDarkMode}
                    onClick={() => toggleGroup("states")}
                  >
                    <LayerGroupTitle $isPrimary $isDarkMode={isDarkMode}>
                      <svg
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        style={{
                          width: "13px",
                          height: "13px",
                          transform: expandedGroups.states
                            ? "rotate(0deg)"
                            : "rotate(-90deg)",
                          transition: "transform 0.2s",
                        }}
                      >
                        <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                      </svg>
                      States
                    </LayerGroupTitle>
                    <LayerGroupActions>
                      {states.length > 0 && (
                        <LayerGroupCount $isPrimary $isDarkMode={isDarkMode}>
                          {states.length}
                        </LayerGroupCount>
                      )}
                      <LayerGroupAddButton
                        $isDarkMode={isDarkMode}
                        title="Add State"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleDrawing();
                        }}
                        style={{
                          background: drawingMode
                            ? isDarkMode
                              ? "rgba(33, 150, 243, 0.3)"
                              : "rgba(33, 150, 243, 0.15)"
                            : undefined,
                          borderColor: drawingMode ? "#2196f3" : undefined,
                          color: drawingMode ? "#2196f3" : undefined,
                        }}
                      >
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                        </svg>
                      </LayerGroupAddButton>
                    </LayerGroupActions>
                  </LayerGroupHeader>
                  {expandedGroups.states && (
                    <LayerList>
                      {(() => {
                        const filteredStates = states.filter((state) =>
                          layerSearchQuery === ""
                            ? true
                            : state.name
                                .toLowerCase()
                                .includes(layerSearchQuery.toLowerCase())
                        );

                        if (filteredStates.length === 0 && layerSearchQuery) {
                          return (
                            <div
                              style={{
                                padding: "20px",
                                textAlign: "center",
                                color: "#888",
                                fontSize: "13px",
                              }}
                            >
                              No states match "{layerSearchQuery}"
                            </div>
                          );
                        }

                        return filteredStates.map((state) => {
                          const isSelected = selectedState?.id === state.id;
                          const isEditingName = editingLayerName === state.id;
                          const isEditing = editingStateId === state.id;
                          return (
                            <ProfessionalLayerItem
                              key={state.id}
                              $selected={isSelected}
                              $visible={true}
                              $isEditing={isEditing}
                              $isDarkMode={isDarkMode}
                              onClick={() => handleStateClick(state)}
                            >
                              <LayerThumbnail
                                $color={state.color}
                                $type="state"
                                title="Drag to reorder"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  // Drag handling removed - not currently used
                                }}
                              />
                              <LayerContent>
                                {isEditingName ? (
                                  <LayerNameInput
                                    $isDarkMode={isDarkMode}
                                    type="text"
                                    value={state.name}
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const updatedState = {
                                        ...state,
                                        name: e.target.value,
                                      };
                                      onStateUpdate(updatedState);
                                    }}
                                    onBlur={() => setEditingLayerName(null)}
                                    onKeyDown={(e) =>
                                      handleLayerNameKeyDown(
                                        e,
                                        state.id,
                                        state.name,
                                        "state"
                                      )
                                    }
                                  />
                                ) : (
                                  <LayerNameDisplay
                                    $isLong={state.name.length > 35}
                                    $isDarkMode={isDarkMode}
                                    onDoubleClick={(e) => {
                                      e.stopPropagation();
                                      handleLayerNameEdit(state.id, state.name);
                                    }}
                                    title={
                                      state.name.length > 35
                                        ? state.name
                                        : undefined
                                    }
                                  >
                                    <LayerNameText
                                      $isLong={state.name.length > 35}
                                    >
                                      {state.name}
                                    </LayerNameText>
                                  </LayerNameDisplay>
                                )}
                                <LayerMetaInfo $isDarkMode={isDarkMode}>
                                  <LayerTypeBadge $type="state">
                                    State
                                  </LayerTypeBadge>
                                  {state.capital && (
                                    <span
                                      style={{
                                        marginLeft: "4px",
                                        color: "#888",
                                        fontSize: "9px",
                                      }}
                                      title={`Capital: ${state.capital}`}
                                    >
                                      📍 {state.capital}
                                    </span>
                                  )}
                                </LayerMetaInfo>
                              </LayerContent>
                              <LayerControls>
                                {isEditing ? (
                                  <LayerControlButton
                                    $active={true}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingStateId(null);
                                    }}
                                    title="Finish Editing"
                                    style={{
                                      color: "#4caf50",
                                      background: "rgba(76, 175, 80, 0.15)",
                                    }}
                                  >
                                    <svg
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                                    </svg>
                                  </LayerControlButton>
                                ) : (
                                  <LayerControlButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingStateId(state.id);
                                    }}
                                    title="Edit shape"
                                  >
                                    <svg
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z" />
                                    </svg>
                                  </LayerControlButton>
                                )}
                                <LayerControlButton
                                  $danger
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      window.confirm(
                                        `Delete "${state.name}"? This cannot be undone.`
                                      )
                                    ) {
                                      onStateDelete(state.id);
                                    }
                                  }}
                                  title="Delete layer"
                                >
                                  <svg viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                    <path
                                      fillRule="evenodd"
                                      d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                                    />
                                  </svg>
                                </LayerControlButton>
                              </LayerControls>
                            </ProfessionalLayerItem>
                          );
                        });
                      })()}
                    </LayerList>
                  )}
                </LayerGroup>

                {/* Guide Layers Group - Secondary Section */}
                <LayerGroup>
                  <LayerGroupHeader
                    $isDarkMode={isDarkMode}
                    onClick={() => toggleGroup("guides")}
                  >
                    <LayerGroupTitle $isDarkMode={isDarkMode}>
                      <svg
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        style={{
                          width: "12px",
                          height: "12px",
                          transform: expandedGroups.guides
                            ? "rotate(0deg)"
                            : "rotate(-90deg)",
                          transition: "transform 0.2s",
                        }}
                      >
                        <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                      </svg>
                      Guide Layers
                    </LayerGroupTitle>
                    <LayerGroupActions>
                      {(guideLayers?.length || 0) > 0 && (
                        <LayerGroupCount $isDarkMode={isDarkMode}>
                          {guideLayers?.length || 0}
                        </LayerGroupCount>
                      )}
                      <LayerGroupAddButton
                        $isDarkMode={isDarkMode}
                        title="Add Guide Layer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddGuideLayerForm(!showAddGuideLayerForm);
                        }}
                        style={{
                          background: showAddGuideLayerForm
                            ? isDarkMode
                              ? "rgba(33, 150, 243, 0.3)"
                              : "rgba(33, 150, 243, 0.15)"
                            : undefined,
                          borderColor: showAddGuideLayerForm
                            ? "#2196f3"
                            : undefined,
                          color: showAddGuideLayerForm ? "#2196f3" : undefined,
                        }}
                      >
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                        </svg>
                      </LayerGroupAddButton>
                    </LayerGroupActions>
                  </LayerGroupHeader>
                  {expandedGroups.guides && (
                    <LayerList>
                      {/* Region Border Layer */}
                      {showBorder !== undefined && setShowBorder && (
                        <ProfessionalLayerItem
                          $selected={false}
                          $visible={showBorder}
                          $isEditing={isEditingBorder || false}
                          $isDarkMode={isDarkMode}
                        >
                          <LayerThumbnail
                            $color="#ef4444"
                            $type="border"
                            title="Region Border"
                          />
                          <LayerContent>
                            <LayerNameDisplay
                              $isLong={false}
                              $isDarkMode={isDarkMode}
                            >
                              <LayerNameText $isLong={false}>
                                🗺️ Region Border
                              </LayerNameText>
                            </LayerNameDisplay>
                            <LayerMetaInfo $isDarkMode={isDarkMode}>
                              <LayerTypeBadge $type="border">
                                {customBorder ? "Custom" : "Official"}
                              </LayerTypeBadge>
                              {isEditingBorder && (
                                <LayerTypeBadge 
                                  $type="guide"
                                  style={{ 
                                    background: isDarkMode ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)",
                                    color: "#3b82f6" 
                                  }}
                                >
                                  Editing
                                </LayerTypeBadge>
                              )}
                            </LayerMetaInfo>
                          </LayerContent>
                          <LayerControls>
                            <LayerControlButton
                              $active={showBorder}
                              $alwaysVisible={true}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowBorder?.(!showBorder);
                              }}
                              title="Toggle visibility"
                            >
                              {showBorder ? (
                                <svg viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                                  <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-.741 0-1.458-.132-2.135-.38l.772.771A7.028 7.028 0 0 0 8 13.5c5 0 8-5.5 8-5.5s-1.06-1.72-3.641-3.238z" />
                                  <path d="M11.534 7.188a4.5 4.5 0 0 0-6.346-6.346l6.346 6.346z" />
                                  <path d="M4.466 8.812a4.5 4.5 0 0 0 6.346 6.346l-6.346-6.346z" />
                                </svg>
                              )}
                            </LayerControlButton>
                            {!isEditingBorder && user?.role === "admin" && onStartEditingBorder && (
                              <LayerControlButton
                                $alwaysVisible={true}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onStartEditingBorder();
                                }}
                                title="Edit shape"
                              >
                                <svg viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z" />
                                </svg>
                              </LayerControlButton>
                            )}
                            {isEditingBorder && onFinishEditingBorder && (
                              <LayerControlButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onFinishEditingBorder();
                                }}
                                title="Finish editing"
                                style={{
                                  color: "#4caf50",
                                  background: "rgba(76, 175, 80, 0.15)",
                                }}
                              >
                                <svg viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                                </svg>
                              </LayerControlButton>
                            )}
                            {customBorder && onDeleteBorder && user?.role === "admin" && (
                              <LayerControlButton
                                $danger
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteBorder();
                                }}
                                title="Delete custom border"
                              >
                                <svg viewBox="0 0 16 16" fill="currentColor">
                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                  <path
                                    fillRule="evenodd"
                                    d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                                  />
                                </svg>
                              </LayerControlButton>
                            )}
                          </LayerControls>
                        </ProfessionalLayerItem>
                      )}

                      {/* Guide Layers */}
                      {(() => {
                        const filteredGuides = (guideLayers || []).filter(
                          (layer: GuideLayer) =>
                            layerSearchQuery === ""
                              ? true
                              : layer.name
                                  .toLowerCase()
                                  .includes(layerSearchQuery.toLowerCase())
                        );

                        if (filteredGuides.length === 0 && layerSearchQuery) {
                          return (
                            <div
                              style={{
                                padding: "20px",
                                textAlign: "center",
                                color: "#888",
                                fontSize: "13px",
                              }}
                            >
                              No guide layers match "{layerSearchQuery}"
                            </div>
                          );
                        }

                        return filteredGuides.map((layer: GuideLayer) => {
                          const selectedId = (
                            selectedGuideLayer as GuideLayer | null
                          )?.id;
                          const isSelected = selectedId === layer.id;
                          const isEditingName = editingLayerName === layer.id;
                          const isEditing = false; // Guide layers don't have shape editing yet
                          return (
                            <ProfessionalLayerItem
                              key={layer.id}
                              $selected={!!isSelected}
                              $visible={layer.visible}
                              $isEditing={isEditing}
                              $isDarkMode={isDarkMode}
                              onClick={() => handleGuideLayerClick(layer)}
                            >
                              <LayerThumbnail
                                $color="#2196f3"
                                $type="guide"
                                title="Drag to reorder"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  // Drag handling removed - not currently used
                                }}
                              />
                              <LayerContent>
                                {isEditingName ? (
                                  <LayerNameInput
                                    $isDarkMode={isDarkMode}
                                    type="text"
                                    value={layer.name}
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      onUpdateGuideLayer?.(layer.id, {
                                        name: e.target.value,
                                      });
                                    }}
                                    onBlur={() => setEditingLayerName(null)}
                                    onKeyDown={(e) =>
                                      handleLayerNameKeyDown(
                                        e,
                                        layer.id,
                                        layer.name,
                                        "guide"
                                      )
                                    }
                                  />
                                ) : (
                                  <LayerNameDisplay
                                    $isLong={layer.name.length > 35}
                                    $isDarkMode={isDarkMode}
                                    onDoubleClick={(e) => {
                                      e.stopPropagation();
                                      handleLayerNameEdit(layer.id, layer.name);
                                    }}
                                    title={
                                      layer.name.length > 35
                                        ? layer.name
                                        : undefined
                                    }
                                  >
                                    <LayerNameText
                                      $isLong={layer.name.length > 35}
                                    >
                                      {layer.name}
                                    </LayerNameText>
                                  </LayerNameDisplay>
                                )}
                                <LayerMetaInfo $isDarkMode={isDarkMode}>
                                  <LayerTypeBadge $type="guide">
                                    Guide
                                  </LayerTypeBadge>
                                </LayerMetaInfo>
                                <LayerOpacityControl>
                                  <LayerOpacitySlider
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={layer.opacity || 0.5}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const newOpacity = parseFloat(
                                        e.target.value
                                      );
                                      onUpdateGuideLayer?.(layer.id, {
                                        opacity: newOpacity,
                                      });
                                    }}
                                  />
                                  <LayerOpacityValue>
                                    {Math.round((layer.opacity || 0.5) * 100)}%
                                  </LayerOpacityValue>
                                </LayerOpacityControl>
                              </LayerContent>
                              <LayerControls>
                                <LayerControlButton
                                  $active={layer.visible}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleGuideLayer?.(layer.id);
                                  }}
                                  title="Toggle visibility"
                                >
                                  {layer.visible ? (
                                    <svg
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                                    </svg>
                                  ) : (
                                    <svg
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-.741 0-1.458-.132-2.135-.38l.772.771A7.028 7.028 0 0 0 8 13.5c5 0 8-5.5 8-5.5s-1.06-1.72-3.641-3.238z" />
                                      <path d="M11.534 7.188a4.5 4.5 0 0 0-6.346-6.346l6.346 6.346z" />
                                      <path d="M4.466 8.812a4.5 4.5 0 0 0 6.346 6.346l-6.346-6.346z" />
                                    </svg>
                                  )}
                                </LayerControlButton>
                                <LayerControlButton
                                  $danger
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      window.confirm(
                                        `Delete "${layer.name}"? This cannot be undone.`
                                      )
                                    ) {
                                      onDeleteGuideLayer?.(layer.id);
                                    }
                                  }}
                                  title="Delete layer"
                                >
                                  <svg viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                                    <path
                                      fillRule="evenodd"
                                      d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                                    />
                                  </svg>
                                </LayerControlButton>
                              </LayerControls>
                            </ProfessionalLayerItem>
                          );
                        });
                      })()}
                    </LayerList>
                  )}
                </LayerGroup>
              </LayerPanel>
            )}
          </>
        )}
      </ContentSection>
    );

    // Render Tools Tab Content
    const renderToolsTab = () => (
      <ContentSection $active={activeTab === "tools"}>
        <Section $isDarkMode={isDarkMode}>
          <SectionHeader $isDarkMode={isDarkMode}>
            <SectionIcon>🛠️</SectionIcon>
            Import & Export
          </SectionHeader>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <SecondaryButton
              $isDarkMode={isDarkMode}
              onClick={() =>
                document.getElementById("import-template-input")?.click()
              }
              style={{ width: "100%" }}
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z" />
              </svg>
              Import JSON
            </SecondaryButton>
            <SecondaryButton
              $isDarkMode={isDarkMode}
              onClick={onExportTemplate}
              style={{ width: "100%" }}
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z" />
              </svg>
              Export JSON
            </SecondaryButton>
          </div>
        </Section>

        <Section $isDarkMode={isDarkMode}>
          <SectionHeader $isDarkMode={isDarkMode}>
            <SectionIcon>📦</SectionIcon>
            Templates
          </SectionHeader>
          <PrimaryButton
            $fullWidth
            onClick={() => setIsTemplateManagerOpen(true)}
          >
            <svg viewBox="0 0 16 16" fill="currentColor">
              <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3A1.5 1.5 0 0 1 15 10.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
            </svg>
            Browse Templates
          </PrimaryButton>
        </Section>
      </ContentSection>
    );

    // Render Settings Tab Content
    const renderSettingsTab = () => (
      <ContentSection $active={activeTab === "settings"}>
        <Section $isDarkMode={isDarkMode}>
          <SectionHeader $isDarkMode={isDarkMode}>
            <SectionIcon>⚙️</SectionIcon>
            Advanced Settings
          </SectionHeader>
          <FormGroup>
            <Label $isDarkMode={isDarkMode}>
              <LabelText>Map Visibility</LabelText>
            </Label>
            <StatusContainer>
              {/* Always show visibility status */}
              <StatusBadge $variant={isPublic ? "success" : "default"}>
                <StatusIcon>{isPublic ? "🌐" : "🔒"}</StatusIcon>
                <StatusText $isDarkMode={isDarkMode}>
                  {isPublic ? "Public" : "Private"}
                </StatusText>
              </StatusBadge>

              {/* Always show approval status separately */}
              {isApproved ? (
                <StatusBadge $variant="info">
                  <StatusIcon>✅</StatusIcon>
                  <StatusText $isDarkMode={isDarkMode}>
                    Approved
                    {approvedVersion && version && version !== approvedVersion
                      ? ` (v${approvedVersion} approved, current v${version})`
                      : version
                      ? ` (v${version})`
                      : ""}
                  </StatusText>
                </StatusBadge>
              ) : approvalRequested ? (
                <StatusBadge $variant="warning">
                  <StatusIcon>⏳</StatusIcon>
                  <StatusText $isDarkMode={isDarkMode}>
                    Pending Approval{version ? ` (v${version})` : ""}
                  </StatusText>
                </StatusBadge>
              ) : isRejected ? (
                <StatusBadge $variant="danger">
                  <StatusIcon>❌</StatusIcon>
                  <StatusText $isDarkMode={isDarkMode}>
                    Rejected{version ? ` (v${version})` : ""}
                  </StatusText>
                </StatusBadge>
              ) : version ? (
                <StatusBadge $variant="default">
                  <StatusIcon>📝</StatusIcon>
                  <StatusText $isDarkMode={isDarkMode}>
                    Draft (v{version})
                  </StatusText>
                </StatusBadge>
              ) : null}
            </StatusContainer>
            {canMakePublic && (
              <ToggleContainer style={{ marginTop: "12px" }}>
                <ToggleLabel $isDarkMode={isDarkMode} htmlFor="public-setting">
                  Make Public
                </ToggleLabel>
                <ToggleSwitch
                  $checked={isPublic || false}
                  htmlFor="public-setting"
                >
                  <ToggleInput
                    type="checkbox"
                    id="public-setting"
                    checked={isPublic || false}
                    onChange={(e) => {
                      setIsPublic?.(e.target.checked);
                    }}
                  />
                </ToggleSwitch>
              </ToggleContainer>
            )}
            {!canMakePublic && (
              <FieldHint style={{ marginTop: "8px", fontSize: "12px" }}>
                Manage approval on the map page
              </FieldHint>
            )}
          </FormGroup>
        </Section>

        <Section $isDarkMode={isDarkMode}>
          <SectionHeader $isDarkMode={isDarkMode}>
            <SectionIcon>🎨</SectionIcon>
            Display Options
          </SectionHeader>
          <FormGroup>
            <ToggleContainer>
              <ToggleLabel $isDarkMode={isDarkMode} htmlFor="capitals-toggle">
                Show capital pins
              </ToggleLabel>
              <ToggleSwitch
                $checked={showCapitals || false}
                $isDarkMode={isDarkMode}
                htmlFor="capitals-toggle"
              >
                <ToggleInput
                  type="checkbox"
                  id="capitals-toggle"
                  checked={showCapitals || false}
                  onChange={(e) => setShowCapitals?.(e.target.checked)}
                />
              </ToggleSwitch>
            </ToggleContainer>
            <ToggleContainer>
              <ToggleLabel
                $isDarkMode={isDarkMode}
                htmlFor="capital-names-toggle"
              >
                Show capital name
              </ToggleLabel>
              <ToggleSwitch
                $checked={showCapitalNames || false}
                $isDarkMode={isDarkMode}
                htmlFor="capital-names-toggle"
              >
                <ToggleInput
                  type="checkbox"
                  id="capital-names-toggle"
                  checked={showCapitalNames || false}
                  onChange={(e) => setShowCapitalNames?.(e.target.checked)}
                />
              </ToggleSwitch>
            </ToggleContainer>
            <ToggleContainer>
              <ToggleLabel
                $isDarkMode={isDarkMode}
                htmlFor="state-names-toggle"
              >
                Show state name
              </ToggleLabel>
              <ToggleSwitch
                $checked={showStateNames || false}
                $isDarkMode={isDarkMode}
                htmlFor="state-names-toggle"
              >
                <ToggleInput
                  type="checkbox"
                  id="state-names-toggle"
                  checked={showStateNames || false}
                  onChange={(e) => setShowStateNames?.(e.target.checked)}
                />
              </ToggleSwitch>
            </ToggleContainer>
            <ToggleContainer>
              <ToggleLabel $isDarkMode={isDarkMode} htmlFor="state-fill-toggle">
                Show state fill colors
              </ToggleLabel>
              <ToggleSwitch
                $checked={showStateFill !== false}
                $isDarkMode={isDarkMode}
                htmlFor="state-fill-toggle"
              >
                <ToggleInput
                  type="checkbox"
                  id="state-fill-toggle"
                  checked={showStateFill !== false}
                  onChange={(e) => setShowStateFill?.(e.target.checked)}
                />
              </ToggleSwitch>
            </ToggleContainer>
            <FormGroup style={{ marginTop: "16px" }}>
              <Label $isDarkMode={isDarkMode}>
                <LabelText>Map Style</LabelText>
              </Label>
              <MapStyleGallery>
                {Object.entries(MAP_STYLES).map(([key, style]) => (
                  <MapStyleCard
                    key={key}
                    $isSelected={(mapStyle || "light") === key}
                    $isDarkMode={isDarkMode}
                    $color={style.color}
                    onClick={() => setMapStyle?.(key as MapStyle)}
                    title={style.description}
                  >
                    <MapStylePreview
                      $color={style.color}
                      $thumbnail={style.thumbnail}
                    />
                    <MapStyleLabel $isDarkMode={isDarkMode}>
                      {style.name}
                    </MapStyleLabel>
                  </MapStyleCard>
                ))}
              </MapStyleGallery>
              <MapStyleDescription $isDarkMode={isDarkMode}>
                {MAP_STYLES[mapStyle || "light"]?.description ||
                  "Choose a map style to change the base map appearance"}
              </MapStyleDescription>
            </FormGroup>
          </FormGroup>
        </Section>
      </ContentSection>
    );

    // Reset loaded map when mapId changes
    useEffect(() => {
      if (mapId !== loadedMapId && mapId) {
        setLoadedMapId(null);
        setVersions([]);
        isLoadingRef.current = false;
        lastLoadedVersionRef.current = null;
        setLoadedVersion(null); // Clear loaded version when switching maps
      }
    }, [mapId, loadedMapId]);

    // Clear loaded version when map is saved (version increments)
    // This happens when user loads a version, makes changes, and saves
    useEffect(() => {
      if (
        version &&
        loadedVersion !== null &&
        previousVersionRef.current !== undefined
      ) {
        // Only clear if the version actually increased from the previous value
        // This means a new version was saved, not that we're loading an older version
        if (version > previousVersionRef.current) {
          setLoadedVersion(null);
        }
      }
      // Update the ref to track the current version for next comparison
      if (version) {
        previousVersionRef.current = version;
      }
    }, [version, loadedVersion]);

    // Reset loaded map when version changes (map was saved) - only if version actually increased
    useEffect(() => {
      if (version && mapId && loadedMapId === mapId && !isLoadingRef.current) {
        // Only reload if version has actually changed to a higher number
        if (
          lastLoadedVersionRef.current === null ||
          version > lastLoadedVersionRef.current
        ) {
          // Version changed, need to reload versions
          setLoadedMapId(null);
          setVersions([]);
          isLoadingRef.current = false;
        }
      }
    }, [version, mapId, loadedMapId]);

    useEffect(() => {
      if (
        activeTab === "versions" &&
        mapId &&
        !isLoadingRef.current &&
        loadedMapId !== mapId
      ) {
        const loadVersions = async () => {
          if (isLoadingRef.current) return; // Prevent duplicate loads
          isLoadingRef.current = true;
          try {
            setLoadingVersions(true);
            const versionsList = await getMapVersions(mapId);
            setVersions(versionsList);
            setLoadedMapId(mapId);
            // Track the current version after loading
            if (version) {
              lastLoadedVersionRef.current = version;
            }
          } catch (error) {
            console.error("Error loading versions:", error);
            toast.error("Failed to load version history");
          } finally {
            setLoadingVersions(false);
            isLoadingRef.current = false;
          }
        };
        loadVersions();
      }
    }, [activeTab, mapId, loadedMapId, version]);

    // Helper function to format relative time
    const getRelativeTime = (dateString: string): string => {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      const diffWeeks = Math.floor(diffMs / 604800000);
      const diffMonths = Math.floor(diffMs / 2592000000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60)
        return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
      if (diffHours < 24)
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
      if (diffDays < 7)
        return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
      if (diffWeeks < 4)
        return `${diffWeeks} week${diffWeeks !== 1 ? "s" : ""} ago`;
      if (diffMonths < 12)
        return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ago`;
      return date.toLocaleDateString();
    };

    // Render Versions Tab Content
    const renderVersionsTab = () => {
      if (!mapId) {
        return (
          <ContentSection
            $active={activeTab === "versions"}
            data-compact="true"
          >
            <Section $isDarkMode={isDarkMode}>
              <SectionHeader $isDarkMode={isDarkMode}>
                <SectionIcon>🕒</SectionIcon>
                Version History
              </SectionHeader>
              <EmptyState $isDarkMode={isDarkMode}>
                <EmptyStateIcon>📝</EmptyStateIcon>
                <EmptyStateText $isDarkMode={isDarkMode}>
                  Version history is only available for saved maps.
                  <br />
                  Save your map to start tracking versions.
                </EmptyStateText>
              </EmptyState>
            </Section>
          </ContentSection>
        );
      }

      return (
        <ContentSection $active={activeTab === "versions"} data-compact="true">
          <Section $isDarkMode={isDarkMode}>
            <SectionHeader $isDarkMode={isDarkMode}>
              <SectionIcon>🕒</SectionIcon>
              Version History
            </SectionHeader>
            {loadingVersions ? (
              <LoadingState $isDarkMode={isDarkMode}>
                <div style={{ fontSize: "18px", marginBottom: "8px" }}>⏳</div>
                <div>Loading version history...</div>
              </LoadingState>
            ) : versions.length === 0 ? (
              <EmptyState $isDarkMode={isDarkMode}>
                <EmptyStateIcon>📋</EmptyStateIcon>
                <EmptyStateText $isDarkMode={isDarkMode}>
                  No version history yet.
                  <br />
                  Versions will be created automatically when you save changes.
                </EmptyStateText>
              </EmptyState>
            ) : (
              <div style={{ padding: "8px 0 16px" }}>
                <VersionTimeline $isDarkMode={isDarkMode}>
                  {versions.map((v, index) => {
                    const isCurrent = version && v.version === version;
                    // Only show "Viewing" if this version is loaded AND it's not the current version
                    const isLoaded = loadedVersion === v.version && !isCurrent;
                    const isApprovedVersion =
                      approvedVersion && v.version === approvedVersion;
                    const isLast = index === versions.length - 1;
                    const prevVersion =
                      index < versions.length - 1 ? versions[index + 1] : null;
                    const diff = prevVersion
                      ? getVersionDiff(prevVersion, v)
                      : null;
                    const relativeTime = getRelativeTime(v.createdAt);
                    const fullTime = new Date(v.createdAt).toLocaleString();

                    const handleLoadVersion = () => {
                      if (onRestoreVersion) {
                        onRestoreVersion(v);
                        setLoadedVersion(v.version);
                        toast.success(
                          `Loaded version ${v.version} on map. Make changes and save to create a new version.`
                        );
                      }
                    };

                    return (
                      <VersionItem
                        key={v.id}
                        $isDarkMode={isDarkMode}
                        $isCurrent={!!isCurrent || isLoaded}
                        $isLast={isLast}
                        onClick={handleLoadVersion}
                        style={{ cursor: "pointer" }}
                      >
                        <VersionCard
                          $isDarkMode={isDarkMode}
                          $isCurrent={!!isCurrent || isLoaded}
                        >
                          <VersionHeader>
                            <VersionTitle $isDarkMode={isDarkMode}>
                              <VersionNumber
                                $isDarkMode={isDarkMode}
                                $isCurrent={!!isCurrent}
                              >
                                v{v.version}
                              </VersionNumber>
                              <VersionBadges>
                                {isCurrent && (
                                  <VersionBadge $variant="current">
                                    Current
                                  </VersionBadge>
                                )}
                                {isLoaded && !isCurrent && (
                                  <VersionBadge $variant="info">
                                    Viewing
                                  </VersionBadge>
                                )}
                                {isApprovedVersion && (
                                  <VersionBadge $variant="approved">
                                    Approved
                                  </VersionBadge>
                                )}
                              </VersionBadges>
                            </VersionTitle>
                          </VersionHeader>

                          <VersionMeta $isDarkMode={isDarkMode}>
                            <VersionTime
                              $isDarkMode={isDarkMode}
                              title={fullTime}
                            >
                              <svg
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                style={{ width: "10px", height: "10px" }}
                              >
                                <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                              </svg>
                              {relativeTime}
                            </VersionTime>
                            <VersionAuthor $isDarkMode={isDarkMode}>
                              <svg
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                style={{ width: "10px", height: "10px" }}
                              >
                                <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                              </svg>
                              {v.createdByDisplayName}
                            </VersionAuthor>
                          </VersionMeta>

                          {v.changeDescription && (
                            <VersionDescription $isDarkMode={isDarkMode}>
                              {v.changeDescription}
                            </VersionDescription>
                          )}
                          {diff &&
                            (diff.statesAdded +
                              diff.statesRemoved +
                              diff.statesModified +
                              diff.guideLayersAdded +
                              diff.guideLayersRemoved >
                              0 ||
                              diff.nameChanged ||
                              diff.descriptionChanged ||
                              diff.thumbnailChanged) && (
                              <DiffSection $isDarkMode={isDarkMode}>
                                <DiffTitle $isDarkMode={isDarkMode}>
                                  <svg
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    style={{ width: "10px", height: "10px" }}
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"
                                    />
                                  </svg>
                                  Changes from v{prevVersion?.version}
                                </DiffTitle>
                                <DiffItems>
                                  {diff.statesAdded > 0 && (
                                    <DiffItem $type="add">
                                      <svg
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                        }}
                                      >
                                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                                      </svg>
                                      {diff.statesAdded} state
                                      {diff.statesAdded !== 1 ? "s" : ""} added
                                    </DiffItem>
                                  )}
                                  {diff.statesRemoved > 0 && (
                                    <DiffItem $type="remove">
                                      <svg
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                        }}
                                      >
                                        <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z" />
                                      </svg>
                                      {diff.statesRemoved} state
                                      {diff.statesRemoved !== 1 ? "s" : ""}{" "}
                                      removed
                                    </DiffItem>
                                  )}
                                  {diff.statesModified > 0 && (
                                    <DiffItem $type="modify">
                                      <svg
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                        }}
                                      >
                                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                                      </svg>
                                      {diff.statesModified} state
                                      {diff.statesModified !== 1
                                        ? "s"
                                        : ""}{" "}
                                      modified
                                    </DiffItem>
                                  )}
                                  {diff.guideLayersAdded > 0 && (
                                    <DiffItem $type="add">
                                      <svg
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                        }}
                                      >
                                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                                      </svg>
                                      {diff.guideLayersAdded} guide layer
                                      {diff.guideLayersAdded !== 1
                                        ? "s"
                                        : ""}{" "}
                                      added
                                    </DiffItem>
                                  )}
                                  {diff.guideLayersRemoved > 0 && (
                                    <DiffItem $type="remove">
                                      <svg
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                        }}
                                      >
                                        <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z" />
                                      </svg>
                                      {diff.guideLayersRemoved} guide layer
                                      {diff.guideLayersRemoved !== 1
                                        ? "s"
                                        : ""}{" "}
                                      removed
                                    </DiffItem>
                                  )}
                                  {diff.nameChanged && (
                                    <DiffItem $type="modify">
                                      <svg
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                        }}
                                      >
                                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                                      </svg>
                                      Name changed
                                    </DiffItem>
                                  )}
                                  {diff.descriptionChanged && (
                                    <DiffItem $type="modify">
                                      <svg
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                        }}
                                      >
                                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                                      </svg>
                                      Description changed
                                    </DiffItem>
                                  )}
                                  {diff.thumbnailChanged && (
                                    <DiffItem $type="modify">
                                      <svg
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                        }}
                                      >
                                        <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                                        <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z" />
                                      </svg>
                                      Thumbnail changed
                                    </DiffItem>
                                  )}
                                </DiffItems>
                              </DiffSection>
                            )}
                        </VersionCard>
                      </VersionItem>
                    );
                  })}
                </VersionTimeline>
              </div>
            )}
          </Section>
        </ContentSection>
      );
    };

    return (
      <PanelContainer $isDarkMode={isDarkMode}>
        <TopBar $isDarkMode={isDarkMode}>
          <TopBarTitle $isDarkMode={isDarkMode}>
            {selectedState
              ? "Edit State"
              : selectedGuideLayer
              ? "Edit Guide Layer"
              : isEditMode
              ? "Edit Map"
              : "Map Editor"}
          </TopBarTitle>
          <TopBarActions>
            {!selectedState && !selectedGuideLayer && (
              <IconButton
                $variant="danger"
                onClick={() => window.history.back()}
                title="Cancel"
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </IconButton>
            )}
          </TopBarActions>
        </TopBar>

        <MainContent>
          <Sidebar $isDarkMode={isDarkMode}>
            <SidebarButton
              $active={activeTab === "map"}
              $isDarkMode={isDarkMode}
              onClick={() => {
                if (tempState || selectedGuideLayer) {
                  handleCancel();
                }
                setActiveTab("map");
              }}
              title="Map Details"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
              </svg>
            </SidebarButton>
            <SidebarButton
              $active={activeTab === "layers"}
              $isDarkMode={isDarkMode}
              onClick={() => setActiveTab("layers")}
              title="Layers"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3A1.5 1.5 0 0 1 15 10.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z" />
              </svg>
            </SidebarButton>
            <SidebarButton
              $active={activeTab === "tools"}
              $isDarkMode={isDarkMode}
              onClick={() => setActiveTab("tools")}
              title="Tools"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1v4a1 1 0 0 1 1 1h1a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-1a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h1a1 1 0 0 1 1-1V4a1 1 0 0 1-1-1H2a1 1 0 0 1-1-1V3zm13 1v1h-1V4h1zm0 5v-1h-1v1h1zm-2-5V4h-1v1h1zm0 5v-1h-1v1h1z" />
              </svg>
            </SidebarButton>
            <SidebarButton
              $active={activeTab === "settings"}
              $isDarkMode={isDarkMode}
              onClick={() => setActiveTab("settings")}
              title="Settings"
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z" />
                <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.292-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.292c.415.764-.42 1.6-1.185 1.184l-.292-.159a1.873 1.873 0 0 0-2.692 1.116l-.094.319c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.693-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.292A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.116l.094-.319z" />
              </svg>
            </SidebarButton>
            {isEditMode && mapId && (
              <SidebarButton
                $active={activeTab === "versions"}
                $isDarkMode={isDarkMode}
                onClick={() => setActiveTab("versions")}
                title="Version History"
              >
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                  <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                </svg>
              </SidebarButton>
            )}
          </Sidebar>

          <ContentPanel $isDarkMode={isDarkMode}>
            {renderMapTab()}
            {renderLayersTab()}
            {renderToolsTab()}
            {renderSettingsTab()}
            {renderVersionsTab()}
          </ContentPanel>
        </MainContent>

        {!selectedState && !selectedGuideLayer && (
          <StickyFooter $isDarkMode={isDarkMode}>
            {(!mapName || mapName.trim() === "" || states.length === 0) && (
              <SaveButtonError $isDarkMode={isDarkMode}>
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                  <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
                </svg>
                <span>
                  {!mapName || mapName.trim() === ""
                    ? "Please enter a map name to save"
                    : states.length === 0
                    ? "Please add at least one state layer to save"
                    : ""}
                </span>
              </SaveButtonError>
            )}
            <SaveButton
              $hasChanges={hasUnsavedChanges}
              onClick={handleSaveMapClick}
              disabled={
                !mapName || mapName.trim() === "" || states.length === 0
              }
              title={
                !mapName || mapName.trim() === ""
                  ? "Please enter a map name"
                  : states.length === 0
                  ? "Please add at least one state"
                  : isEditMode
                  ? "Update Map (Ctrl/Cmd + S)"
                  : "Save Map (Ctrl/Cmd + S)"
              }
            >
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 0 1 .708-.708L7.5 9.293V1.5A.5.5 0 0 1 8 1z" />
              </svg>
              {hasUnsavedChanges && <span>•</span>}
              {isEditMode ? "Update Map" : "Save Map"}
            </SaveButton>
          </StickyFooter>
        )}

        <TemplateManager
          isOpen={isTemplateManagerOpen}
          onClose={() => setIsTemplateManagerOpen(false)}
          onTemplateSelect={onTemplateSelect}
          isApproved={isApproved}
          isPublic={isPublic}
        />

        <input
          id="import-template-input"
          type="file"
          style={{ display: "none" }}
          onChange={onImportTemplate}
          accept=".json"
        />
      </PanelContainer>
    );
  }
);

export default StatePanel;
